// i18n messages
import { ref, watch } from 'vue'

const currentLocale = ref<'en' | 'zh'>('en')

// 让 <html lang> 跟随当前语言：既有利于无障碍与 SEO，
// 也让 CSS 能按语言微调排版（见 main.scss 里 html[lang^='zh'] 的中文标题字重）
watch(
  currentLocale,
  (l) => {
    document.documentElement.lang = l === 'zh' ? 'zh-CN' : 'en'
  },
  { immediate: true, flush: 'sync' },
)

const messages = {
  en: {
    common: {
      search: 'Search',
      create: 'Create',
      edit: 'Edit',
      delete: 'Delete',
      cancel: 'Cancel',
      confirm: 'Confirm',
      save: 'Save',
      upload: 'Upload',
      download: 'Download',
      noData: 'No data',
      tip: 'Tip',
      success: 'Success',
      error: 'Error',
      deleted: 'Deleted',
      total: 'Total',
      public: 'Public',
      private: 'Private',
      offline: 'Offline',
      action: 'Action',
      view: 'View',
      all: 'All',
      clear: 'Clear',
      collapse: 'Collapse',
      overview: 'Overview',
      versions: 'Versions',
      files: 'Files',
      size: 'Size',
      name: 'Name',
      description: 'Description',
      createdAt: 'Created At',
      version: 'Version',
      gpu: 'GPU',
      tags: 'Tags',
      repository: 'Repository',
      login: 'Log In',
      register: 'Sign Up',
      logout: 'Logout',
      settings: 'Settings',
      repositories: 'Repositories',
      notifications: 'Notifications',
      noNotifications: 'No notifications',
      today: 'Today',
      yesterday: 'Yesterday',
      daysAgo: 'days ago',
      weeksAgo: 'weeks ago',
      monthsAgo: 'months ago',
      yearsAgo: 'years ago',
      darkMode: 'Dark Mode',
      lightMode: 'Light Mode',
      actions: 'Actions',
      saved: 'Saved',
      copy: 'Copy',
      copied: 'Copied!',
      or: 'or',
    },
    auth: {
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      verificationCode: 'Verification Code',
      sendCode: 'Send Code',
      forgotPassword: 'Forgot Password?',
      loginSuccess: 'Log in successful',
      registerSuccess: 'Sign up successful',
      loginFailed: 'Log in failed',
      codeSent: 'Verification code sent',
      emailRequired: 'Email is required',
      passwordRequired: 'Password is required',
      codeRequired: 'Verification code is required',
      captchaTitle: 'Security Verification',
      captchaRefresh: 'Refresh',
      captchaPassed: 'Verified',
      captchaFailed: 'Verification failed, please try again',
      emailAlreadyExists: 'Email address already exists',
      slideToVerify: 'Slide to verify',
      invalidEmail: 'Invalid email format',
      passwordMinLength: 'Password must be at least 8 characters with letters and numbers',
      registerWithGitHub: 'Sign Up with GitHub',
      passwordLogin: 'Password Log In',
      emailCodeLogin: 'Email Code Log In',
      loginTitle: 'Log In',
      loginSubtitle: 'Sign in to manage your kernels, models and team',
      registerTitle: 'Create an Account',
      registerSubtitle: 'Create an account and join the Flash Hub embodied AI ecosystem',
      haveAccount: 'Already have an account?',
      noAccount: 'Don\'t have an account?',
      resetPasswordTitle: 'Reset Password',
      resetPasswordDesc: 'Enter your email to receive a verification code',
      sendResetCode: 'Send Reset Code',
      newPassword: 'New Password',
      resetPassword: 'Reset Password',
      backToLogin: 'Back to Log In',
      enterEmail: 'Enter your email',
      enterPassword: 'Enter your password',
      enterCode: 'Enter verification code',
      passwordConfirm: 'Confirm your password',
      passwordMin: 'Enter your password (min 6 characters)',
      or: 'Or',
      passwordMismatch: 'Passwords do not match',
      brandTitle: 'Edge AI App Distribution Community',
      brandSubtitle: 'Connecting chips, models and robot bodies with the FlashRT high-performance inference engine at the core — accelerating perception, decision and action in the real world.',
      featureRepositories: 'FlashRT High-Performance Kernels',
      featurePlatforms: 'Multi-Platform Models',
      featureVersions: 'Multi-Arch GPU Inference',
      githubLoggingIn: 'Logging in with GitHub...',
      githubWait: 'Please wait while we complete the authentication process.',
      githubCodeMissing: 'Missing authorization code',
      agreeTo: 'I agree to the',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      and: 'and',
      agreeTermsRequired: 'You must agree to the terms of service',
      passwordComplexity: 'Password must be at least 8 characters with letters and numbers',
      weak: 'Weak',
      medium: 'Medium',
      strong: 'Strong',
      rememberMe: 'Remember me',
      logoutSuccess: 'Logged out successfully',
    },
    profile: {
      basicInfo: 'Basic Information',
      basicInfoDesc: 'Update your personal information and public profile',
      username: 'Username',
      displayName: 'Display Name',
      bio: 'Bio',
      contactInfo: 'Contact Information',
      verified: 'Verified',
      website: 'Website',
      websitePlaceholder: 'Enter your website URL',
      company: 'Company',
      location: 'Location',
      securitySettings: 'Security Settings',
      securityDesc: 'Manage your password and account security',
      changePassword: 'Change Password',
      setPassword: 'Set Password',
      currentPassword: 'Current Password',
      currentPasswordRequired: 'Current password is required',
      tokenNameRequired: 'API key name is required',
      scopesRequired: 'At least one scope is required',
      newPassword: 'New Password',
      passwordTip: 'At least 8 characters, containing letters and numbers',
      confirmPassword: 'Confirm Password',
      passwordNotMatch: 'Passwords do not match',
      passwordUpdated: 'Password updated successfully',
      updatePassword: 'Update Password',
      accessTokens: 'API Keys',
      tokensDesc: 'Manage API keys for programmatic access',
      createToken: 'Create API Key',
      tokenName: 'Name',
      tokenCreated: 'API key created successfully',
      tokenWarning: 'Save your API key somewhere safe. You will not be able to see it again after you close this modal. If you lose it, you\'ll have to create a new one.',
      deleteTokenConfirm: 'Are you sure you want to delete this API key?',
      scopes: 'Scopes',
      key: 'Key',
      status: 'Status',
      scopeRepoRead: 'Read repositories',
      scopeRepoWrite: 'Write repositories',
      scopeOrgRead: 'Read organizations',
      scopeOrgWrite: 'Write organizations',
      scopeGpuRead: 'Read GPU models',
      scopePlatformRead: 'Read platforms',
      lastUsed: 'Last Used',
      languageSettings: 'Language Settings',
      languageDesc: 'Choose your preferred language',
      chinese: '中文',
      english: 'English',
      uploadAvatar: 'Upload Avatar',
      avatarTip: 'Recommended size: 200x200px, supports JPG and PNG',
    },
    repository: {
      createRepository: 'Create Repository',
      myRepositories: 'My Repositories',
      repositoryName: 'Repository Name',
      selectPlatform: 'Select platform',
      selectOrganization: 'Select organization',
      selectGPU: 'Select supported GPU models',
      repositoryCreated: 'Repository created successfully',
      repositoryUpdated: 'Repository updated',
      repositoryDeleted: 'Repository deleted',
      manageRepository: 'Edit Repository',
      manageVersions: 'Manage Versions',
      createVersion: 'Create Version',
      preRelease: "Pre-release",
      released: "Released",
      releaseStatus: 'Release Status',
      preReleaseTip: 'Click to publish - once released, cannot revert',
      releasedTip: 'Released - no modifications allowed',
      clickToToggle: 'Click to toggle visibility',
      makePrivateConfirm: 'This will hide the version from public view. Continue?',
      makePublicConfirm: 'This will make the version visible to everyone. Continue?',
      releaseConfirmTitle: 'Confirm Release',
      releaseConfirmDesc: 'Once released, this version cannot be modified and the release status cannot be changed back to pre-release. Are you sure?',
      confirmRelease: 'Confirm Release',
      uploadFile: 'Upload Files',
      uploadFiles: 'Upload Files',
      versionCreated: 'Version created successfully',
      versionDeleted: 'Version deleted',
      preview: 'Preview',
      latestVersions: 'Latest Versions',
      allVersions: 'All Versions',
      versionFiles: 'Version Files',
      deleteConfirm: 'Are you sure you want to delete this repository? All versions will be deleted.',
      deleteVersionConfirm: 'Are you sure you want to delete this version?',
      noRepositories: 'No repositories yet',
      createFirst: 'Create your first repository',
      selectFiles: 'Select Files',
      selectFolder: 'Select Folder',
      onlySoFiles: 'Only .so, .py, .zip, .json files are allowed',
      pleaseSelectFiles: 'Please select files to upload',
      viewFiles: 'View Files',
      noReadme: 'No README provided',
      noVersions: 'No versions yet',
      repoNameTip: 'Use letters, numbers, hyphens, dots and underscores',
      gpuTip: 'Select all GPU models this repository supports',
      readmeTip: 'Markdown format supported',
      descriptionPlaceholder: 'Enter repository description (Markdown supported)',
      enterRepoName: 'Enter repository name',
      enterModelName: 'Enter model name (e.g., bert-base-uncased)',
      readmePlaceholder: 'Enter README content (Markdown supported)',
      versionStatus: 'Version Status',
      dropFiles: 'Drop .so, .py, .zip or .json files here',
      uploadTo: 'Upload to',
      rootDir: 'Root',
      basicInfo: 'Basic Information',
      nameAvailable: 'This name is available',
      nameInvalid: 'Only alphanumeric characters, \"-\", \".\" and \"_\" supported',
      nameTooShort: 'Name must be at least 2 characters',
      publicDesc: 'Anyone can view this repository',
      privateDesc: 'Only you can view this repository',
      visibility: 'Visibility',
      backToVersions: 'Back to Versions',
      uploadTip: 'Only .so, .py, .zip, .json files are accepted',
      platformModels: 'Platform & Model',
      platformModelsRequired: 'At least 1 platform and model is required',
      addPlatformModel: '+ Add platform & model',
      duplicatePlatformModel: 'Duplicate platform and model combination',
      selectTags: 'Select tags',
      versionNumber: 'Version Number',
      versionPlaceholder: 'e.g., v1.0.0',
      versionTip: 'Use semantic versioning for clarity',
      publicVersionDesc: 'Anyone can download this version',
      privateVersionDesc: 'Only you can download this version',
      versionRequired: 'Version number is required',
      versionInfo: 'Version Information',
    },
    kernel: {
      kernels: 'Kernels',
      heroTitle: 'Flash Hub Kernels',
      heroSubtitle: 'High-performance compute kernels for physical AI — ready to load across multi-chip platforms.',
      public: 'Public',
      useThisKernel: 'Use this kernel',
      supportedHardwares: 'Supported hardwares',
      os: 'OS',
      arch: 'Arch',
      howToUse: 'How to use from the Kernels library',
      myKernels: 'My Kernels',
      uploadKernel: 'Upload Kernel',
      selectFolder: 'Select build directory',
      folderTip: 'Select the build/<variant>/ directory. Directory structure is preserved.',
      dragFolderTip: 'Drag the build directory here to keep directory structure',
      metadataMissing: 'metadata.json not found in selection',
      kernelName: 'Kernel Name',
      kernelNameImmutableTip: 'Kernel name is set by metadata.json and cannot be changed.',
      variantName: 'Variant Name',
      descriptionPlaceholder: 'Enter a short description (optional)',
      visibility: 'Visibility',
      publicDesc: 'Anyone can view and download',
      privateDesc: 'Only you can access',
      selectOrganization: 'Select organization',
      selectTags: 'Select tags (optional)',
      publish: 'Publish',
      publishing: 'Publishing...',
      published: 'Kernel published successfully',
      updated: 'Kernel updated',
      uploadVariant: 'Upload Variant Package',
      viewVariants: 'View Variants',
      variantAdded: 'Variant package uploaded successfully',
      variantsDetected: '{count} variant packages detected',
      packageFileTooLarge: 'The variant package contains a file over 1GB. The whole package cannot be uploaded.',
      variantUploadInvalid: 'The selected folder does not contain a valid metadata.json',
      variantVersionMismatch: 'metadata.json name/version must match this kernel version',
      noFolderSelected: 'Please select a folder or files to upload',
      license: 'License',
      latestVersion: 'Latest Version',
      archs: 'Supported Architectures',
      pythonDepends: 'Python Dependencies',
      versions: 'Versions',
      noKernels: 'No kernels yet',
      createFirst: 'Upload your first kernel',
      deleteConfirm: 'Are you sure you want to delete this kernel? All versions and files will be deleted.',
      deleteVersionConfirm: 'Are you sure you want to delete this version? All variants and files will be deleted.',
      deleteVariantConfirm: 'Are you sure you want to delete this variant?',
      deleteFileConfirm: 'Are you sure you want to delete this file?',
      viewFiles: 'View Files',
      versionDeleted: 'Version deleted',
      kernelDeleted: 'Kernel deleted',
      manageKernels: 'Manage Kernels',
      metadataPreview: 'Metadata Preview',
      kernelId: 'Kernel ID',
      lastUpdated: 'Last Updated',
      backendType: 'Backend',
      digest: 'Digest',
      variantImmutableTip: 'The same (version, variant) can only be published once. Bump the version in build.toml and rebuild to republish.',
      versionNumber: 'Version Number',
      manageVersions: 'Manage Versions',
    },
    home: {
      // Hero
      heroEyebrow: '<span>EMBODIED AI PLATFORM</span><b>Full-Stack Embodied AI Solutions</b>',
      heroTitle: 'Bring <span>intelligence</span><br/>into the real world',
      heroSub: 'Connecting chips, models and robot bodies — with the FlashRT high-performance inference engine at the core, accelerating every perception, decision and action in the real world.',
      heroExplore: 'Explore Platform',
      heroViewSolutions: 'View Solutions',
      heroStatPlatforms: 'Compute Platforms',
      heroStatModels: 'Models Adapted',
      heroStatKernels: 'High-Perf Kernels',
      heroVisualAria: 'FlashHub tech-stack illustration',
      heroCardMultimodal: 'Multimodal Models',
      heroCardCompute: 'Heterogeneous Compute',
      heroCardEmbodied: 'Embodied Robots',
      // Manifesto
      manifestoTitle: 'One platform, end-to-end<br/>from <span>compute to action</span>',
      platformLayerHardware: 'Hardware Abstraction',
      platformLayerHardwareDesc: 'Unified heterogeneous compute interface',
      platformLayerEngine: 'FlashRT Engine',
      platformLayerEngineDesc: 'Kernel fusion & graph execution',
      platformLayerServing: 'Model Serving',
      platformLayerServingDesc: 'VLA / VLM / world models',
      platformLayerRobot: 'Robot SDK',
      platformLayerRobotDesc: 'Perception, control & data loop',
      // Ecosystem
      ecoTitle: 'An Open Embodied Ecosystem',
      ecoDesc: 'Componentize leading hardware, frontier models and robot bodies — build embodied apps like building blocks.',
      ecoChipsTitle: 'Compute Chips',
      ecoChipsDesc: 'Heterogeneous compute from cloud to edge',
      ecoModelsTitle: 'Model Hub',
      ecoModelsDesc: 'Ready-to-use embodied & physical AI models',
      ecoBodiesTitle: 'Robot Bodies',
      ecoBodiesDesc: 'Mainstream forms, unified device access',
      chipStatePriority: 'Priority',
      chipStateSupported: 'Supported',
      chipStateInProgress: 'In Progress',
      chipStatePlanned: 'Planned',
      chipStateVerifying: 'Verifying',
      chipSpecThor: 'Blackwell GPU · Embodied compute',
      chipSpecOrin: 'Ampere GPU · Edge inference',
      chipSpecAscend: 'Atlas · Domestic compute',
      chipSpecHorizon: 'BPU · On-device AI',
      chipSpecRB5: 'QRB5165 · Robotics',
      chipSpecIntel: 'CPU + GPU + NPU',
      modelDescPI0: 'General-purpose robot vision-language-action model',
      modelDescGR00T: 'Foundation model for humanoid robots',
      modelDescCosmos: 'Physical AI world foundation model platform',
      modelDescOpenVLA: 'Open vision-language-action model',
      modelDescRDT: 'Dual-arm robot diffusion policy model',
      modelDescQwen: 'Perception, understanding & task planning base',
      robotOrgAGIBOT: 'AGIBOT',
      robotOrgUnitree: 'Unitree',
      robotOrgDeepRobotics: 'DeepRobotics',
      robotOrgUBTECH: 'UBTECH',
      robotOrgGalbot: 'Galbot',
      robotTypeHumanoidWheeled: 'Humanoid / Wheeled',
      robotTypeHumanoid: 'Humanoid',
      robotTypeQuadruped: 'Quadruped',
      robotTypeIndustrial: 'Industrial humanoid',
      robotTypeWheeledArm: 'Wheeled dual-arm',
      robotTypeCobot: 'Collaborative arm',
      // FlashRT
      flashrtTitle: 'An <span>extreme engine</span><br/>for physical AI',
      rtMetricFfn: 'End-to-end FFN speedup',
      rtMetricKernels: 'Operators & fused kernels',
      rtMetricOverhead: 'Runtime scheduling overhead',
      kernelPanelTitle: 'Kernel & Runtime SO Catalog',
      kernelPanelSub: 'Deployable runtime components per chip',
      chipFilterAll: 'All Chips',
      kernelColComponent: 'Component / Kernel',
      kernelColType: 'Type',
      kernelColSo: 'Runtime SO',
      kernelColTargets: 'Target Chips',
      kernelColStatus: 'Status',
      kernelRepoLink: 'View FlashRT-HF-kernels on GitHub',
      // Solutions
      solutionsTitle: 'Solutions for the Real World',
      solutionsDesc: 'More than components. We combine models, compute and bodies into verifiable, replicable, operable industry solutions.',
      solCoffeeTitle: 'Unmanned Coffee Retail',
      solCoffeeMeta: 'Embodied operation · Chain retail',
      solCoffeeDesc: 'From ordering and cup handling to brewing, delivery and restock alerts — keep standardized beverage service running 24/7.',
      solCoffeeFlow1: 'Order recognition',
      solCoffeeFlow2: 'Precise manipulation',
      solCoffeeFlow3: 'Exception handover',
      solInspectTitle: 'Commercial Complex Inspection',
      solInspectMeta: 'Multimodal perception · Property mgmt',
      solInspectDesc: 'Autonomous patrol detecting fire, equipment, environment and crowd anomalies, closing the loop with the ticketing platform.',
      solInspectFlow1: 'Autonomous navigation',
      solInspectFlow2: 'Anomaly detection',
      solInspectFlow3: 'Closed-loop tickets',
      solCareTitle: 'Smart Elderly Care',
      solCareMeta: 'Human-robot interaction · Care',
      solCareDesc: 'Companionship, assisted vitals monitoring, fall alerts and item delivery for faster care response.',
      solCareFlow1: 'Natural interaction',
      solCareFlow2: 'Risk alerts',
      solCareFlow3: 'Daily assistance',
      solFactoryTitle: 'Flexible Industrial Operations',
      solFactoryMeta: 'VLA · Manufacturing',
      solFactoryDesc: 'For loading, sorting, inspection and assembly — one platform cuts cross-line migration and redevelopment cost.',
      solFactoryFlow1: 'Teach & learn',
      solFactoryFlow2: 'Flexible grasping',
      solFactoryFlow3: 'Quality traceability',
      learnMore: 'Learn More',
      // Developer
      devTitle: 'Start with one kernel<br/>build your embodied app',
      devDesc: 'SDKs, model recipes, performance benchmarks, deployment guides and open source — every step from validation to production is traceable.',
      devGithub: 'Visit GitHub',
      devBrowse: 'Browse Components',
      // Contact
      contactTitle: "Let's bring embodied intelligence<br/>to the next site.",
      contactDesc: 'Tell us your industry, robot body and target tasks — get full guidance from PoC to scale.',
      contactBtn: 'Book a Consultation',
      // Footer
      footerTagline: 'Edge AI App Distribution Community',
      footerTaglineSub: '',
      footerColPlatform: 'Platform',
      footerLinkChips: 'Compute Chips',
      footerLinkModels: 'Model Hub',
      footerLinkBodies: 'Robot Bodies',
      footerColTech: 'Technology',
      footerLinkEngine: 'FlashRT Engine',
      footerLinkDev: 'Developer Center',
      footerColSolutions: 'Solutions',
      footerLinkRetail: 'Retail',
      footerLinkInspection: 'Inspection',
      footerLinkCare: 'Care',
      footerColContact: 'Contact',
      footerLinkBusiness: 'Business',
      footerLinkAbout: 'About Us',
      footerTag: 'Edge AI App Distribution Community',
    },
    docs: {
      title: 'Kernel Documentation',
      subtitle: 'Directory structure, metadata.json fields and build variant naming for FlashHub kernels (compatible with the Hugging Face Kernels spec).',
      viewBuildGuide: 'View build documentation',
    },
    dashboard: {
      allRepos: 'All Repositories',
      latestVersion: 'Latest Version',
      sortBy: 'Sort By',
      sortByUpdated: 'Updated Time',
      sortByCreated: 'Created Time',
      sortByDownloads: 'Downloads',
    },
    organization: {
      createOrganization: 'Create Organization',
      basicInfo: 'Basic Information',
      orgName: 'Organization Name',
      displayName: 'Display Name',
      description: 'Description',
      logo: 'Logo',
      uploadLogo: 'Upload Logo',
      logoTip: 'Recommended size: 200x200px, supports JPG, PNG',
      contactInfo: 'Contact Information',
      website: 'Website',
      email: 'Email',
      location: 'Location',
      enterOrgName: 'Enter organization name',
      enterDisplayName: 'Enter display name',
      displayNameTip: 'This name will be publicly visible, supports Chinese and special characters',
      descriptionPlaceholder: 'Enter organization description (max 500 characters)',
      descriptionTip: 'Briefly describe your organization\'s purpose and focus',
      enterWebsite: 'Enter website URL',
      enterContactEmail: 'Enter contact email',
      enterLocation: 'Enter organization location',
      visibility: 'Visibility',
      publicOrg: 'Public Organization',
      privateOrg: 'Private Organization',
      publicOrgDesc: 'Anyone can find and view this organization',
      privateOrgDesc: 'Only members can view this organization',
      members: 'Members',
      inviteMembers: 'Invite Members',
      role: 'Role',
      name: 'Name',
      joinedAt: 'Joined At',
      emails: 'Email Addresses',
      emailsPlaceholder: 'Enter email addresses, separated by comma, semicolon, or new line',
      emailsTip: 'You can enter multiple email addresses at once',
      emailsRequired: 'Please enter at least one email address',
      sendInvites: 'Send Invitations',
      invitesSent: 'Invitations sent successfully',
      rolePermissions: 'Role Permissions',
      ownerPerms: 'Full access: manage org, members, repos, delete org',
      adminPerms: 'Manage members, create repos',
      developerPerms: 'Create and manage repos',
      viewerPerms: 'Read-only access to repos',
      createFirst: 'Create First Organization',
      deleteConfirm: 'This action cannot be undone. Are you sure?',
      deleted: 'Organization deleted',
      settingsSaved: 'Settings saved successfully',
      checking: 'Checking availability...',
      nameAvailable: 'This name is available',
      nameTaken: 'This name is already taken',
      nameInvalid: 'Only letters, numbers, hyphens and underscores allowed',
      orgNameHint: 'Only letters, numbers, hyphens and underscores allowed',
      orgNameRequired: 'Organization name is required',
      displayNameRequired: 'Display name is required',
      orgCreated: 'Organization created successfully',
      noDescription: 'No description provided',
      editSettings: 'Edit Organization',
      organizations: 'My Organizations',
      backToOrganizations: 'Back to Organizations',
      noOrganizations: 'No organizations yet',
      organizationDetail: 'Organization Details',
    },
    public: {
      publicRepos: 'Public Repositories',
      searchPlaceholder: 'Search repositories by name...',
      heroTitle: 'AI Model Inference Runtime',
      heroSubtitle: 'Deploy and run AI models with FlashRT — fast, portable, production-ready inference.',
      sortNewest: 'Newest',
      sortDownloads: 'Most Downloads',
      sortStars: 'Most Stars',
      noDescription: 'No description provided',
      noTags: 'No tags',
      emptyHint: 'Create your first repository to get started',
      downloads: 'Downloads',
      stars: 'Stars',
      forks: 'Forks',
      all: 'All',
    },
    nav: {
      home: 'Home',
      apps: 'Apps',
      models: 'Models',
      kernels: 'Kernels',
      news: 'News',
      ecosystem: 'Ecosystem',
      cliDesc: 'Distribution CLI for FlashRT',
      agentName: 'AI Assistant',
      agentDesc: 'Publish models with AI assistance',
      rtDesc: 'High-performance inference engine',
    },
    agent: {
      title: 'FlashHub AI Assistant',
      desc: 'Let AI automatically list models on FlashHub',
      chooseMode: 'Choose Execution Mode',
      standard: 'Standard',
      standardDesc: 'AI asks for your confirmation before each step.',
      auto: 'Auto',
      autoDesc: 'AI completes the listing directly without step-by-step confirmation, then reports the result.',
      promptLabel: 'Instruction to send to AI',
      standardPrompt: 'Read https://flashhub.aodianyun.com/agent/AGENTS.md and use USER_KEY=fh_xxx to list the model on FlashHub',
      autoPrompt: 'Read https://flashhub.aodianyun.com/agent/AGENTS-auto.md and use USER_KEY=fh_xxx to list the model on FlashHub',
      hint: 'Works with pi / Claude Code / Codex / opencode and any AI Agent. AI will automatically: query GPU models/platforms/tags → create organization → create repository → create version → upload files.',
      apiKeyTitle: 'User API Key',
      apiKeyDesc: 'Create it in User Center → API Keys, with the following scopes:',
    },
    apps: {
      title: 'Apps',
      subtitle: 'Browse general agents, specialized agents and audio & video tools by scenario.',
      install: 'Install FlashCLI',
      all: 'All',
      count: 'apps',
      view: 'View App',
      tool: 'Tool',
      toolTag: 'Tool · site-wide',
      toolDesc: 'Find, install, run and update models and apps; versions follow release notes.',
    },
    homeFx: {
      kickerA: 'From Lab to Production',
      kickerB: 'Edge AI Community',
      h1a: 'Explore Edge AI',
      h1b: 'Apps, Models & Chips',
      h1c: ' at Peak Performance',
      heroDesc: 'Start from a use case to explore model plans and hardware adaptation, then run on your device via FlashCLI. You can also start from an existing model or hardware.',
      searchPh: 'Search apps, models, chips or devices',
      search: 'Search',
      exploreApps: 'Explore Apps',
      browseModels: 'Browse Models',
      iHaveHw: 'I have hardware',
      hcApp: 'App',
      hcModels: 'Models',
      hcDevice: 'Device · Chip',
      hcStatus: 'Status',
      hcCta: 'View app & plans',
      s1Eyebrow: '01 · APPS',
      s1Title: 'Start with an app',
      s1Desc: 'Explore app capabilities to find the models and hardware you need.',
      viewAllApps: 'View all apps',
      dependsOn: 'Models',
      adaptedDevices: 'Devices',
      viewApp: 'View App',
      s2Eyebrow: '02 · MODELS',
      s2Title: 'Choose a model for your app',
      s2Desc: 'Browse capabilities, related apps and adapted hardware by task.',
      viewAllModels: 'View all models',
      all: 'All',
      relatedApps: 'Related apps',
      adaptedHw: 'Adapted hardware',
      viewModel: 'View Model',
      none: 'None',
      s3Eyebrow: '03 · HARDWARE',
      s3Title: 'What AI apps can your hardware run?',
      s3Desc: 'Pick a device to see runnable apps and adapted models.',
      viewAllHw: 'View all hardware',
      brandFilter: 'Filter by brand',
      otherChips: 'Other chips · no devices or records yet',
      addHw: 'Add device or chip',
      currentDevice: 'Current device',
      chipSpecs: 'Chip specs',
      deviceDetail: 'Device details',
      runnableApps: 'Runnable apps',
      pendingApps: 'Pending apps',
      adaptedModels: 'Adapted models',
      adaptationRecord: 'Record',
      noReadyApps: 'No fully verified apps for this device.',
      noRecords: 'No adaptation records for this device.',
      verifiedNote: 'Model adaptation and app-level status are verified separately',
      s4Eyebrow: '04 · QUICK START',
      s4Title: 'Run your chosen app on your device',
      s4Desc: 'Commands match the selected app, plan, device and version; otherwise docs and requests are provided.',
      step1t: 'Choose an app',
      step1d: 'Pick an app by its outcome',
      step2t: 'Confirm model & hardware plan',
      step2d: 'Switch plans and devices in the app detail',
      step3t: 'Get & run via FlashCLI',
      step3d: 'Copy the command for the matching combo',
      installCli: 'Install FlashCLI',
      quickStart: 'Quick start',
      app: 'App',
      plan: 'Plan',
      device: 'Device',
      version: 'Version',
      adjustPlan: 'Adjust plan in app details',
      noCmdTitle: 'No reliable install command for this combo',
      viewDocs: 'View docs',
      applyAdaptation: 'Request adaptation',
      s5Eyebrow: '05 · UPDATES',
      s5Title: 'Latest adaptations',
      communityEyebrow: 'COMMUNITY',
      communityTitle: 'Community',
      enterCommunity: 'Enter community',
      noNeed: "Didn't find what you need?",
      submitRequest: 'Submit a request',
      newVerified: 'new verified adaptation',
      submittedVersion: 'submitted adaptation version',
      replies: 'replies',
    },
    news: {
      heading: 'Insights & Updates',
      subtitle: 'Product releases, engineering deep dives, and embodied AI trends.',
      searchPlaceholder: 'Search news by title...',
      all: 'All News',
      empty: 'No news yet',
      emptyHint: 'Please check back later',
      views: 'views',
      categories: {
        release: 'Product',
        tech: 'Engineering',
        case: 'Case Study',
        insight: 'Industry',
      },
      back: 'Back to News',
      notFound: 'Article not found',
      notFoundHint: 'The article may have been removed or unpublished.',
    },
    validation: {
      required: 'This field is required',
    },
    confirm: {
      deleteTitle: 'Confirm Delete',
      deleteMessage: 'Are you sure you want to delete this item?',
      logoutTitle: 'Confirm',
      logoutMessage: 'Are you sure you want to logout?',
    },
    notFound: {
      title: '404',
      subtitle: 'Page not found',
      backHome: 'Back to Home',
    },
    footer: {
      resources: 'Resources',
      company: 'Company',
      contact: 'Contact',
      docs: 'Documentation',
      help: 'Help Center',
      community: 'Community',
      about: 'About Us',
      careers: 'Careers',
    },
    flashcli: {
      subtitle: 'Distribution CLI for FlashRT — one command to fetch a Model Bundle, install dependencies, download weights, and run inference.',
      featuresTitle: 'Core Capabilities',
      quickstart: {
        title: 'Quick Start',
        install: 'Install',
      },
      commands: {
        run: { label: 'run', desc: 'Run VLA inference with a single command' },
        pull: { label: 'pull', desc: 'Pre-fetch model weights from Hugging Face' },
        models: { label: 'models', desc: 'List catalog and check cache status' },
        doctor: { label: 'doctor', desc: 'Environment and GPU diagnostic check' },
      },
      features: {
        bundle: { title: 'Bundle Distribution', desc: 'Auto-detect GPU and fetch the right runtime bundle from CDN — no manual setup.' },
        gpu: { title: 'GPU Auto-Matching', desc: 'Matches your NVIDIA GPU architecture (SM89, SM120) to the correct pre-built bundle variant.' },
        autodeps: { title: 'Auto Dependency Install', desc: 'Reads bundle metadata and installs Python dependencies, model weights, and tokenizers automatically.' },
        oneclick: { title: 'One-Click Inference', desc: 'A single flashcli run command handles bundle download, weight fetch, and inference launch.' },
        cache: { title: 'Local Caching', desc: 'Bundles cached at ~/.flashcli/bundles, weights at ~/.flashcli/models — never re-download.' },
        cli: { title: 'Full CLI Toolkit', desc: 'Subcommands for run, pull, doctor, bundle management, and environment diagnostics.' },
      },
      commandRefTitle: 'Command Reference',
      ref: {
        run: 'Run preset inference (e.g. pi05_libero VLA task)',
        pull: 'Pre-fetch Hugging Face weights only',
        modelsList: 'Show catalog presets and local cache status',
        modelsEnvs: 'Display GPU environment matching for a preset',
        doctor: 'Check GPU, Python, and system compatibility',
        bundleSync: 'Pre-download / update the runtime bundle',
        bundleValidate: 'Validate a local bundle directory layout',
      },
      cacheTitle: 'Local Cache',
      cache: {
        bundles: 'Unpacked runtime bundle files',
        models: 'Hugging Face model weights',
        tokenizer: 'PaliGemma tokenizer (post_pull)',
      },
    },
    admin: {
      management: 'Management',
      resourceManagement: 'Resource Listing',
      resourceNew: 'New Resource Listing',
      resourceEdit: 'Edit Resource Listing',
      resourceDetailTitle: 'Resource Listing Detail',
      resource: {
        desc: 'Collect application, model and chip/hardware information, assign owners and track listing preparation.',
        all: 'All',
        app: 'Applications',
        model: 'Models',
        hardware: 'Chips / Hardware',
        searchPlaceholder: 'Search name, team or contact',
        statusAll: 'All statuses',
        ownerAll: 'All owners',
        originAll: 'All sources',
        mineOnly: 'Assigned to me',
        clearFilters: 'Clear filters',
        total: 'Total',
        items: 'items',
        incomplete: 'Incomplete',
        dueToday: 'Due today or overdue',
        draft: 'Draft',
        export: 'Export',
        create: 'New Application',
        columnName: 'Resource Name',
        columnType: 'Type',
        columnOrg: 'Team / Vendor',
        columnCompleteness: 'Completeness',
        columnStatus: 'Status',
        columnRsiStatus: 'RSI Status',
        columnOwner: 'Owner',
        columnContact: 'Contact',
        columnContactWay: 'Contact Info',
        columnNextAction: 'Next Action',
        columnNextDate: 'Next Follow-up',
        columnActions: 'Actions',
        exportFilename: 'resource-listings',
        exportSuccess: 'Exported {count} records',
        deleteConfirm: 'Are you sure you want to delete "{name}"? This cannot be undone.',
        deleteTitle: 'Delete Resource Listing',
        deleted: 'Deleted',
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',
        notFilled: 'Not filled',
        unassigned: 'Unassigned',
        noData: 'No resource listing applications',
        completenessComplete: 'Complete',
        completenessIncomplete: 'Incomplete',
        missing: {
          intro: 'One-line introduction',
          org: 'Team / Vendor',
          websiteOrRepo: 'Website or project URL',
          sources: 'Source links',
          detail: 'Introduction',
          appCategory: 'Application category',
          appUsage: 'Main use cases',
          appModels: 'Related models',
          appDeploy: 'Deployment',
          appOs: 'OS & runtime',
          appHardware: 'Hardware requirement',
          modelCategory: 'Model category',
          modelName: 'Model name and version',
          modelWeights: 'Weights URL',
          modelLicense: 'License',
          modelDeployNotes: 'Deployment notes',
          modelHardwares: 'Related chips / hardware',
          gpuSeries: 'GPU series',
          arch: 'Architecture ID',
          gpuVendor: 'GPU vendor',
          backend: 'Compute backend',
          deviceType: 'Device type',
          coreSpecs: 'Core specs',
          specSheet: 'Spec sheet',
          sdk: 'SDK / tools',
          contact: 'Contact person',
        },
        origin: {
          internal: 'Internal collection',
          external: 'External provided',
        },
        logWays: {
          wechat: 'WeChat',
          email: 'Email',
          phone: 'Phone',
          meeting: 'Meeting',
          other: 'Other',
        },
        status: {
          pending: 'Pending',
          collecting: 'Collecting',
          reviewing: 'Reviewing',
          ready: 'Ready',
          hold: 'On Hold',
        },
        rsiStatus: {
          idle: 'Waiting',
          pending: 'Pending',
          processing: 'Processing',
          done: 'Done',
          failed: 'Failed',
        },
      },
      resourceDetail: {
        back: 'Resource Listing',
        draft: 'Draft',
        noIntro: 'No one-line introduction yet',
        more: 'More',
        delete: 'Delete',
        edit: 'Edit',
        addLog: 'Add Communication Log',
        basicInfo: 'Basic Information',
        teamVendor: 'Team / Vendor',
        origin: 'Source',
        website: 'Official Website',
        repo: 'Project URL',
        detail: 'Introduction',
        typeSpecific: 'Type-specific Information',
        appCategory: 'Application Category',
        deploy: 'Deployment',
        usage: 'Main Use Cases',
        appModels: 'Related Models',
        osEnv: 'OS & Runtime',
        hardwareRequirement: 'Hardware Requirement',
        demoUrl: 'Demo URL',
        modelCategory: 'Model Category',
        modelName: 'Model Name',
        modelVersion: 'Version',
        modelParams: 'Parameters',
        modelPrecision: 'Precision / Quantization',
        license: 'License',
        weights: 'Weights URL',
        knownApps: 'Known Apps',
        knownHardware: 'Known Hardware',
        deployNotes: 'Deployment Notes',
        modelHardwares: 'Related Chips / Hardware',
        gpuSeries: 'GPU Series',
        arch: 'Architecture ID',
        gpuVendor: 'GPU Vendor',
        backend: 'Compute Backend',
        deviceType: 'Device Type',
        specSheet: 'Spec Sheet',
        sdk: 'SDK / Tools',
        supportedModels: 'Supported Models',
        runningApps: 'Running Apps',
        adaptationNotes: 'Adaptation Notes',
        coreSpecs: 'Core Specifications',
        unnamed: 'Unnamed',
        notFilled: 'Not filled',
        attachmentsSources: 'Attachments & Sources',
        sources: 'Sources',
        attachments: 'Attachments',
        noAttachments: 'No attachments',
        commLog: 'Communication Log',
        noCommLog: 'No communication logs',
        withTarget: 'With',
        recordedBy: 'Recorded by',
        nextStep: 'Next step',
        followPanel: 'Follow-up Panel',
        currentStatus: 'Status',
        rsiStatus: 'RSI Status',
        retryRsi: 'Resubmit RSI',
        missingItems: 'Missing items',
        complete: 'Complete',
        owner: 'Owner',
        contact: 'Contact',
        nextAction: 'Next Action',
        nextDate: 'Next Follow-up Date',
        note: 'Internal Note',
        unassigned: 'Unassigned',
        logTime: 'Time',
        logTarget: 'Contact',
        logTargetPlaceholder: 'Name or team',
        logWay: 'Method',
        logConclusion: 'Conclusion',
        logConclusionPlaceholder: 'What was confirmed, what was provided, what remains',
        logNext: 'Next Step',
        logSync: 'Sync next step and date to follow-up panel',
        logFine: 'Manual record only. No message will be sent automatically.',
        saveLog: 'Save Log',
        logRequired: 'Please enter conclusion',
        logSaved: 'Communication log saved',
        statusUpdated: 'Status updated to',
        retryConfirm: 'Resubmit "{name}" to RSI?',
        retryTitle: 'Resubmit RSI',
        retryConfirmBtn: 'Resubmit',
        retrySaved: 'Resubmitted, status changed to pending',
        deleteConfirm: 'Delete "{name}"? This cannot be undone.',
        deleteTitle: 'Delete Resource Listing',
        deleted: 'Deleted',
        recordNotFound: 'Record not found',
        backToList: 'Back to list',
      },
      resourceForm: {
        backNew: 'Back to list',
        backEdit: 'Back to detail',
        newTitle: 'New Resource Listing',
        editTitle: 'Edit: {name}',
        saveHint: 'You can save drafts when information is incomplete. Required: resource type, name, and at least one source link.',
        typeSwitch: 'Switches by resource type.',
        name: 'Resource Name',
        namePlaceholder: 'e.g. Offline meeting transcription tool',
        intro: 'One-line Introduction',
        introPlaceholder: '20–40 characters describing what it is and the problem it solves',
        orgLabel: 'Team / Company / Vendor',
        sources: 'Source Links',
        addSource: 'Add source link',
        sourceHelp: 'Where the information comes from, e.g. official docs, spec pages, provided materials.',
        detail: 'Detailed Introduction',
        detailPlaceholder: 'Features, technical approach, cooperation background, etc.',
        attachments: 'Attachment Upload',
        chooseFile: 'Choose Files',
        attachmentsHelp: 'Only file names are recorded; files are not actually uploaded.',
        appCategory: 'Application Category',
        appDeploy: 'Deployment',
        appUsage: 'Main Use Cases',
        appModels: 'Related Models',
        modelResource: 'Model resource',
        addModel: 'Add Model',
        appModelsHelp: 'Select from existing model resources. Multiple selections are supported.',
        osEnv: 'OS & Runtime',
        appHardware: 'Hardware Requirement or Existing Device',
        demoUrl: 'Demo URL',
        modelCategory: 'Model Category',
        modelName: 'Specific Model Name',
        modelVersion: 'Version',
        modelParams: 'Parameters',
        modelPrecision: 'Precision / Quantization',
        weights: 'Weights URL',
        license: 'License URL',
        knownApps: 'Known Apps',
        knownHardware: 'Known Hardware',
        deployNotes: 'Deployment Notes',
        modelHardwares: 'Related Chips / Hardware',
        chipHardwareResource: 'Chip / hardware resource',
        addChipHardware: 'Add Chip/Hardware',
        modelHardwaresHelp: 'Select from existing chip/hardware resources. Multiple selections are supported.',
        gpuSeries: 'GPU Series',
        arch: 'Architecture ID',
        gpuVendor: 'GPU Vendor',
        backend: 'Compute Backend',
        deviceType: 'Device Type',
        coreSpecs: 'Core Specifications',
        specName: 'Parameter name, e.g. Memory',
        specValue: 'Parameter value, e.g. 12GB GDDR6',
        addSpec: 'Add Parameter',
        specSheet: 'Spec Sheet',
        sdk: 'SDK / Tooling URL',
        supportedModels: 'Supported Models',
        runningApps: 'Running Apps',
        adaptationNotes: 'Adaptation Notes',
        followUpDesc: 'The internal owner is our follow-up person; the contact person is the provider\'s counterpart.',
        owner: 'Internal Owner',
        ownerPlaceholder: 'Follow-up owner name',
        currentStatus: 'Status',
        contactName: 'Contact Person',
        contactNamePlaceholder: 'Use "Not contacted" if no contact yet',
        contactTitle: 'Title / Team',
        contactWay: 'Contact Method',
        contactWayPlaceholder: 'WeChat / Email / Phone / Community',
        nextDate: 'Next Follow-up Date',
        nextAction: 'Next Action',
        nextActionPlaceholder: 'e.g. Request spec sheet, confirm chip/hardware adaptation list',
        note: 'Internal Note',
        progressTitle: 'Progress',
        missingCount: 'Still missing {count} items',
        complete: 'Information basically complete',
        draftHint: 'Drafts can be saved anytime; missing items will be shown again before submission.',
        lastUpdated: 'Last updated {date}',
        newRecord: 'New record',
        cancel: 'Cancel',
        saveDraft: 'Save Draft',
        submitReview: 'Submit for Review',
        confirmTitle: 'Information Still Missing',
        confirmLead: 'The following information has not been filled in. Reviewers may return it for completion:',
        backFix: 'Back to Edit',
        submitAnyway: 'Submit Anyway',
        pickerModelTitle: 'Select Related Models',
        pickerHardwareTitle: 'Select Related Chips/Hardware',
        searchModel: 'Search model name',
        searchHardware: 'Search chip/hardware name',
        noModel: 'No model resources',
        noHardware: 'No chip/hardware resources',
        confirm: 'Confirm',
        nameRequired: 'Please enter resource name',
        sourcesRequired: 'Please enter at least one source link',
        fillRequired: 'Please fill in the required fields first',
        draftSaved: 'Draft saved',
        submitted: 'Submitted for review',
        typeApp: 'Runnable application or tool',
        typeModel: 'Model, version and weights',
        typeHardware: 'GPU, chip, board or complete hardware solution',
        modelNamePlaceholder: 'e.g. SenseVoice-Small',
        modelVersionPlaceholder: 'e.g. 2024-07',
        modelParamsPlaceholder: 'e.g. 1.5B',
        modelPrecisionPlaceholder: 'e.g. FP16 / INT4',
        deployNotesPlaceholder: 'e.g. Export to ONNX first, then convert to the target NPU format.',
        gpuSeriesPlaceholder: 'e.g. NVIDIA RTX 30 Series',
        archPlaceholder: 'e.g. sm86',
        gpuVendorPlaceholder: 'e.g. Nvidia',
        gpuVendorOtherPlaceholder: 'Enter the specific GPU vendor',
        backendPlaceholder: 'e.g. cuda',
        backendOtherPlaceholder: 'Enter the specific compute backend',
        deviceTypePlaceholder: 'e.g. gpu',
        deviceTypeOtherPlaceholder: 'Enter the specific device type',
        specSheetPlaceholder: 'File name or URL',
        supportedModelsPlaceholder: 'e.g. YOLOv8n (vendor tested)',
        select: 'Please select',
      },
      model: {
        desc: 'Manage model repositories, organizations, and GPU / platform / tag catalogs.',
        tabRepos: 'Model Repositories',
        tabOrgs: 'Organizations',
        tabCatalog: 'GPU / Platforms / Tags',
        searchRepo: 'Search repository name',
        searchOrg: 'Search organization name',
        allStatus: 'All statuses',
        public: 'Public',
        private: 'Private',
        offline: 'Offline',
        search: 'Search',
        repoName: 'Repository',
        org: 'Organization',
        platformModel: 'Platform Model',
        gpu: 'GPU',
        status: 'Status',
        createdAt: 'Created At',
        actions: 'Actions',
        takeOffline: 'Offline',
        putOnline: 'Online',
        delete: 'Delete',
        orgName: 'Organization Name',
        displayName: 'Display Name',
        repoKernelCount: 'Models/Kernels',
        memberCount: 'Members',
        enabled: 'Enabled',
        disabled: 'Disabled',
        disable: 'Disable',
        enable: 'Enable',
        addGpu: 'Add GPU',
        addPlatform: 'Add Platform',
        addTag: 'Add Tag',
        gpuId: 'GPU Identifier',
        description: 'Description',
        sortOrder: 'Sort Order',
        edit: 'Edit',
        platformId: 'Identifier',
        tagId: 'Identifier',
        tagNameCn: 'Chinese Name',
        color: 'Color',
        gpuNamePlaceholder: 'e.g. sm89-cu130-linux-x86_64-py312',
        gpuDescPlaceholder: 'e.g. GeForce RTX 40 Series, L40S Series',
        platformNamePlaceholder: 'e.g. huggingface',
        platformDisplayPlaceholder: 'e.g. HuggingFace',
        tagNamePlaceholder: 'e.g. LLM',
        tagIconPlaceholder: 'e.g. ri-robot-line',
        tagColorPlaceholder: 'e.g. #2563eb',
        gpuTitle: 'GPU Model',
        platformTitle: 'Platform',
        tagTitle: 'Tag',
        editGpuTitle: 'Edit GPU Model',
        addGpuTitle: 'Add GPU Model',
        editPlatformTitle: 'Edit Platform',
        addPlatformTitle: 'Add Platform',
        editTagTitle: 'Edit Tag',
        addTagTitle: 'Add Tag',
        gpuIdHelp: 'GPU Identifier (naming: <arch>-<runtime>-<os>-<cpuarch>-py<python>)',
        icon: 'Icon URL',
        iconHelp: 'Icon (remixicon class)',
        save: 'Save',
        cancel: 'Cancel',
        updated: 'Updated',
        deleted: 'Deleted',
        saved: 'Saved',
        deleteTitle: 'Delete Confirmation',
        deleteRepoConfirm: 'Delete repository "{name}"?',
        deleteOrgConfirm: 'Delete organization "{name}"? This will also affect its models and kernels.',
        deleteGpuConfirm: 'Delete GPU "{name}"?',
        deletePlatformConfirm: 'Delete platform "{name}"?',
        deleteTagConfirm: 'Delete tag "{name}"?',
      },
      newsManagement: 'News Management',
      userManagement: 'User Management',
      modelManagement: 'Model Management',
      apiKeys: {
        title: 'API Keys',
        desc: 'Manage API keys for programmatic access to open endpoints.',
        create: 'Create API Key',
        name: 'Name',
        namePlaceholder: 'e.g. Resource Sync',
        key: 'Key',
        scopes: 'Scopes',
        scopeResourcesRead: 'Read resource listing applications',
        scopeResourcesWrite: 'Update resource status',
        scopeGpuWrite: 'Manage GPU models',
        status: 'Status',
        enabled: 'Enabled',
        disabled: 'Disabled',
        lastUsed: 'Last Used',
        createdAt: 'Created At',
        actions: 'Actions',
        delete: 'Delete',
        deleteConfirm: 'Delete this API key? This cannot be undone.',
        createdTitle: 'API Key Created',
        createdHint: 'Save your API key somewhere safe. You will not be able to see it again after you close this modal. If you lose it, you\'ll have to create a new one.',
        copy: 'Copy',
        copied: 'Copied',
        noKeys: 'No API keys yet',
      },
      news: {
        new: 'New Article',
        edit: 'Edit Article',
        title: 'Title',
        titlePlaceholder: 'Enter the article title',
        category: 'Category',
        categoryPlaceholder: 'Select a category',
        cover: 'Cover Image',
        uploadCover: 'Upload Cover',
        coverHint: 'Recommended 16:9 ratio, JPG/PNG/WebP',
        summary: 'Summary',
        summaryPlaceholder: 'Optional. Leave blank to auto-generate from content',
        autoSummary: 'Auto from content:',
        useAutoSummary: 'Use',
        autoSummaryEmpty: 'Add content to auto-generate a summary',
        content: 'Content',
        contentPlaceholder: 'Write the article...',
        publishTime: 'Publish Time',
        publishTimeHint: 'Leave blank to use the current time',
        status: 'Status',
        published: 'Published',
        draft: 'Draft',
        searchPlaceholder: 'Search by title',
        colTitle: 'Title',
        colCategory: 'Category',
        colPublishTime: 'Publish Time',
        colViews: 'Views',
        colStatus: 'Status',
        colActions: 'Actions',
        empty: 'No articles yet',
        editBtn: 'Edit',
        preview: 'Preview',
        deleteBtn: 'Delete',
        deleteConfirmTitle: 'Delete Article',
        deleteConfirmMessage: 'This cannot be undone. The cover and all images inside the article will be deleted too.',
        save: 'Save',
        saving: 'Saving...',
        saveDraft: 'Save draft',
        draftSaved: 'Saved as draft',
        createFailed: 'Failed to create draft',
        createSuccess: 'Article published',
        untitled: 'Untitled draft',
        updateSuccess: 'Article updated',
        deleteSuccess: 'Article deleted',
        titleRequired: 'Title is required',
        contentRequired: 'Content is required',
        loadFailed: 'Failed to load the article',
      },
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password (min 8 chars, 1 letter + 1 digit)',
      dashboard: 'Dashboard',
      user: 'User',
      emailVerified: 'Verified',
      status: 'Status',
      registeredAt: 'Registered',
      actions: 'Actions',
      verified: 'Verified',
      unverified: 'Unverified',
      adminRole: 'Admin Role',
      primaryAdmin: 'Primary Admin',
      adminTag: 'Admin',
      setAdmin: 'Set Admin',
      removeAdmin: 'Remove Admin',
      setAdminSuccess: 'Admin granted',
      removeAdminSuccess: 'Admin removed',
      confirmRemoveAdmin: "Remove this user's admin permission?",
      detail: {
        title: 'User Details',
        id: 'ID',
        username: 'Username',
        email: 'Email',
        displayName: 'Display Name',
        verified: 'Email Verified',
        status: 'Status',
        bio: 'Bio',
        website: 'Website',
        company: 'Company',
        location: 'Location',
        registeredAt: 'Registered At',
        updatedAt: 'Updated At',
      },
      totalUsers: 'Total Users',
      active: 'Active',
      disabled: 'Disabled',
      searchPlaceholder: 'Search email, username...',
      noUsers: 'No users',
      confirmDisableUser: 'Are you sure you want to disable this user?',
      disable: 'Disable',
      enable: 'Enable',
      enableSuccess: 'User enabled',
      disableSuccess: 'User disabled',
      passwordRequired: 'Please fill in both fields',
      passwordLength: 'Password must be at least 8 characters',
      passwordChanged: 'Password changed successfully',
      // Admin auth related
      adminLogin: 'Admin Login',
      adminEmail: 'Admin Email',
      password: 'Password',
      login: 'Login',
      loginFailed: 'Login failed, check email and password',
      loginSuccess: 'Login successful',
      backHome: 'Back to Home',
      fillEmailPassword: 'Please enter email and password',
      analytics: {
        title: 'Download Analytics',
        totalDownloads: 'Total Downloads',
        avgDaily: 'Avg Daily',
        activeDays: 'Active Days',
        dailyDownloadTrend: 'Daily Download Trend',
        downloads: 'Downloads',
        byRepo: 'By Repository',
        '7d': '7d',
        '14d': '14d',
        '30d': '30d',
        '90d': '90d',
        noData: 'No download data yet',
        pvuvTitle: 'PV / UV Traffic',
        pvuvDesc: 'PV = total page views. UV = unique visitors, same device/browser counts as 1',
        site: 'Analytics',
        avgPvPerUv: 'PV/UV',
        newVisitors: 'New',
        bounceRate: 'Bounce',
        cardPVDesc: 'Total page views in period. ↑↓ vs previous period',
        cardUVDesc: 'Distinct visitors in period. ↑↓ vs previous period',
        avgPvPerUvDesc: 'PV÷UV, avg pages per visitor',
        cardBounceDesc: '% who left after 1 page, lower is better',
        visitorComposition: 'Visitors',
        visitorDesc: 'New: first-time visitors ever. Returning: visitors who have been here before',
        returningVisitors: 'Returning',
        engagement: 'Engagement',
        engageDesc: 'PV/UV = avg pages per visitor (higher=more engaged). Bounce rate = % who left after 1 page (lower=better)',
        trafficSource: 'Traffic Source',
        sourceDesc: 'Where visitors came from: Direct (typed URL/bookmark), Search (Google/Bing/Baidu), GitHub, or other sites',
        device: 'Device',
        deviceDesc: 'Device types detected from browser user-agent: Desktop, Mobile, or Tablet',
        country: 'Region',
        countryDesc: 'Geographic distribution by IP. China shows province-level, other countries show country-level. Powered by ip2region',
        hourly: 'Hourly Traffic',
        hourlyDesc: 'Page views by hour of day (server time). Helps identify peak traffic hours',
        domain: 'Domain',
        landingPages: 'Landing Pages',
        landingDesc: 'First page a visitor lands on in this period. Shows which pages attract traffic',
        referers: 'Referrers',
        referersDesc: 'External websites that sent traffic to your site',
        retentionTitle: 'Retention',
        retention: 'Day-1 Retention',
        retentionDesc: '% of visitors who returned the next day. Higher = better user retention',
        desktop: 'Desktop',
        mobile: 'Mobile',
        tablet: 'Tablet',
        direct: 'Direct',
        search: 'Search',
        other: 'Other',
        page: 'Page',
      },
    },
  },
  zh: {
    common: {
      search: '搜索',
      create: '创建',
      edit: '编辑',
      delete: '删除',
      cancel: '取消',
      confirm: '确认',
      save: '保存',
      upload: '上传',
      download: '下载',
      noData: '暂无数据',
      tip: '提示',
      success: '成功',
      error: '错误',
      deleted: '已删除',
      total: '总计',
      public: '公开',
      private: '私有',
      offline: '已下架',
      action: '操作',
      view: '查看',
      all: '全部',
      clear: '清空',
      collapse: '收起',
      overview: '概览',
      versions: '版本',
      files: '文件',
      size: '大小',
      name: '名称',
      description: '描述',
      createdAt: '创建时间',
      version: '版本',
      gpu: 'GPU',
      tags: '标签',
      repository: '仓库',
      login: '登录',
      register: '注册',
      logout: '退出',
      settings: '设置',
      repositories: '仓库',
      notifications: '通知',
      noNotifications: '暂无通知',
      today: '今天',
      yesterday: '昨天',
      daysAgo: '天前',
      weeksAgo: '周前',
      monthsAgo: '个月前',
      yearsAgo: '年前',
      darkMode: '深色模式',
      lightMode: '浅色模式',
      actions: '操作',
      saved: '已保存',
      copy: '复制',
      copied: '已复制',
      or: '或',
    },
    auth: {
      email: '邮箱',
      password: '密码',
      confirmPassword: '确认密码',
      verificationCode: '验证码',
      sendCode: '发送验证码',
      forgotPassword: '忘记密码？',
      loginSuccess: '登录成功',
      registerSuccess: '注册成功',
      loginFailed: '登录失败',
      codeSent: '验证码已发送',
      emailRequired: '邮箱必填',
      passwordRequired: '密码必填',
      codeRequired: '验证码必填',
      captchaTitle: '安全验证',
      captchaRefresh: '换一张',
      captchaPassed: '验证通过',
      captchaFailed: '验证失败，请重试',
      emailAlreadyExists: '邮箱地址已经存在',
      slideToVerify: '向右滑动滑块完成验证',
      invalidEmail: '邮箱格式不正确',
      passwordMinLength: '密码至少8位，需包含字母和数字',
      registerWithGitHub: 'GitHub 注册',
      passwordLogin: '密码登录',
      emailCodeLogin: '验证码登录',
      loginTitle: '登录',
      loginSubtitle: '登录后管理您的内核、模型与团队',
      registerTitle: '创建账户',
      registerSubtitle: '创建账户，加入 Flash Hub 具身智能生态',
      haveAccount: '已有账户？',
      noAccount: '没有账户？',
      resetPasswordTitle: '重置密码',
      resetPasswordDesc: '输入邮箱获取验证码',
      sendResetCode: '发送重置验证码',
      newPassword: '新密码',
      resetPassword: '重置密码',
      backToLogin: '返回登录',
      enterEmail: '请输入邮箱',
      enterPassword: '请输入密码',
      enterCode: '请输入验证码',
      passwordConfirm: '请确认密码',
      passwordMin: '请输入密码（至少6个字符）',
      or: '或者',
      passwordMismatch: '两次密码输入不一致',
      brandTitle: '端侧AI应用分发社区',
      brandSubtitle: '以 FlashRT 高性能推理引擎为核心，连接芯片、模型与机器人本体，为真实世界的感知、决策与行动加速。',
      featureRepositories: 'FlashRT 高性能内核',
      featurePlatforms: '多平台模型',
      featureVersions: '多架构 GPU 推理',
      githubLoggingIn: '正在通过 GitHub 登录...',
      githubWait: '请等待我们完成认证过程。',
      githubCodeMissing: '缺少授权码',
      agreeTo: '我已阅读并同意',
      terms: '服务条款',
      privacy: '隐私政策',
      and: '和',
      agreeTermsRequired: '请先同意服务条款和隐私政策',
      passwordComplexity: '密码至少需要8位，且包含字母和数字',
      weak: '弱',
      medium: '中',
      strong: '强',
      rememberMe: '记住我',
      logoutSuccess: '已退出登录',
    },
    profile: {
      basicInfo: '基本信息',
      basicInfoDesc: '更新您的个人信息和公开资料',
      username: '用户名',
      displayName: '显示名称',
      bio: '个人简介',
      contactInfo: '联系信息',
      verified: '已验证',
      website: '个人网站',
      websitePlaceholder: '请输入网站地址',
      company: '公司',
      location: '位置',
      securitySettings: '安全设置',
      securityDesc: '管理您的密码和账户安全',
      changePassword: '修改密码',
      setPassword: '设置密码',
      currentPassword: '当前密码',
      currentPasswordRequired: '请输入当前密码',
      tokenNameRequired: '请输入 API 密钥名称',
      scopesRequired: '请至少选择一个权限范围',
      newPassword: '新密码',
      passwordTip: '至少8位，包含字母和数字',
      confirmPassword: '确认密码',
      passwordNotMatch: '两次密码输入不一致',
      passwordUpdated: '密码修改成功',
      updatePassword: '更新密码',
      accessTokens: 'API 密钥',
      tokensDesc: '管理用于程序化访问的 API 密钥',
      createToken: '创建 API 密钥',
      tokenName: '名称',
      tokenCreated: 'API 密钥创建成功',
      tokenWarning: '请妥善保存此 API 密钥。关闭此窗口后将无法再次查看。如果丢失，需要重新创建。',
      deleteTokenConfirm: '确定要删除此 API 密钥吗？',
      scopes: '权限范围',
      key: '密钥',
      status: '状态',
      scopeRepoRead: '读取仓库',
      scopeRepoWrite: '写入仓库',
      scopeOrgRead: '读取组织',
      scopeOrgWrite: '写入组织',
      scopeGpuRead: '读取 GPU 型号',
      scopePlatformRead: '读取平台',
      lastUsed: '最后使用',
      languageSettings: '语言设置',
      languageDesc: '选择您偏好的语言',
      chinese: '中文',
      english: 'English',
      uploadAvatar: '上传头像',
      avatarTip: '推荐尺寸：200x200px，支持 JPG、PNG',
    },
    repository: {
      createRepository: '创建仓库',
      myRepositories: '我的仓库',
      repositoryName: '仓库名称',
      selectPlatform: '选择平台',
      selectOrganization: '请选择组织',
      selectGPU: '选择支持的 GPU 型号',
      repositoryCreated: '仓库创建成功',
      repositoryUpdated: '仓库更新成功',
      repositoryDeleted: '仓库删除成功',
      manageRepository: '编辑仓库',
      manageVersions: '版本管理',
      createVersion: '创建版本',
      uploadFile: '上传文件',
      preRelease: "预发布",
      released: "已发布",
      releaseStatus: '发布状态',
      preReleaseTip: '点击发布 - 发布后不可逆',
      releasedTip: '已发布 - 不允许修改',
      clickToToggle: '点击切换可见性',
      makePrivateConfirm: '将隐藏该版本，前台不再显示。确定吗？',
      makePublicConfirm: '将公开该版本，所有人可见。确定吗？',
      releaseConfirmTitle: '确认发布',
      releaseConfirmDesc: '发布后该版本将无法再进行任何修改，且发布状态不可逆。确定要发布吗？',
      confirmRelease: '确认发布',
      uploadFiles: '上传文件',
      versionCreated: '版本创建成功',
      versionDeleted: '版本删除成功',
      preview: '预览',
      latestVersions: '最新版本',
      allVersions: '所有版本',
      versionFiles: '版本文件',
      deleteConfirm: '确定要删除此仓库吗？所有版本都将被删除。',
      deleteVersionConfirm: '确定要删除此版本吗？',
      noRepositories: '暂无仓库',
      createFirst: '创建您的第一个仓库',
      selectFiles: '选择文件',
      selectFolder: '选择文件夹',
      onlySoFiles: '仅允许 .so、.py、.zip、.json 文件',
      pleaseSelectFiles: '请选择要上传的文件',
      viewFiles: '查看文件',
      noReadme: '未提供 README',
      noVersions: '暂无版本',
      repoNameTip: '使用字母、数字、连字符、点和下划线',
      gpuTip: '选择此仓库支持的所有 GPU 型号',
      readmeTip: '支持 Markdown 格式',
      descriptionPlaceholder: '请输入仓库描述（支持 Markdown）',
      enterRepoName: '请输入仓库名称',
      enterModelName: '请输入模型名称（如 bert-base-uncased）',
      readmePlaceholder: '请输入 README 内容（支持 Markdown）',
      versionStatus: '版本状态',
      dropFiles: '将 .so、.py、.zip 或 .json 文件拖到此处',
      uploadTo: '上传至',
      rootDir: '根目录',
      basicInfo: '基本信息',
      nameAvailable: '该名称可用',
      nameInvalid: '仅支持字母、数字、"-"、"." 和 "_"',
      nameTooShort: '名称至少需要2个字符',
      publicDesc: '任何人都可以查看此仓库',
      privateDesc: '仅您可以查看此仓库',
      visibility: '可见性',
      backToVersions: '返回版本列表',
      uploadTip: '仅接受 .so、.py、.zip、.json 文件',
      platformModels: '平台与模型',
      platformModelsRequired: '至少需要1个平台和模型',
      addPlatformModel: '+ 添加平台与模型',
      duplicatePlatformModel: '平台与模型组合重复',
      selectTags: '选择标签',
      versionNumber: '版本号',
      versionPlaceholder: '例如 v1.0.0',
      versionTip: '建议使用语义化版本号以便管理',
      publicVersionDesc: '任何人都可以下载此版本',
      privateVersionDesc: '仅您可以下载此版本',
      versionRequired: '版本号为必填项',
      versionInfo: '版本信息',
    },
    kernel: {
      kernels: '内核',
      heroTitle: 'Flash Hub 内核',
      heroSubtitle: '为物理 AI 加速的高性能计算内核，覆盖多芯片平台，即装即用。',
      public: '公开',
      useThisKernel: '使用此内核',
      supportedHardwares: '支持的硬件',
      os: '操作系统',
      arch: '架构',
      howToUse: '如何从 Kernels 库中使用',
      myKernels: '我的内核',
      uploadKernel: '上传内核',
      selectFolder: '选择构建目录',
      folderTip: '选择 build/<variant>/ 目录，将保留目录结构。',
      dragFolderTip: '拖拽构建目录到此处以保留目录结构',
      metadataMissing: '所选文件中未找到 metadata.json',
      kernelName: '内核名称',
      kernelNameImmutableTip: '内核名称由 metadata.json 决定，不可修改。',
      variantName: '变体名称',
      descriptionPlaceholder: '输入简短描述（可选）',
      visibility: '可见性',
      publicDesc: '任何人都可以查看和下载',
      privateDesc: '仅您可以访问',
      selectOrganization: '选择组织',
      selectTags: '选择标签（可选）',
      publish: '发布',
      publishing: '发布中...',
      published: '内核发布成功',
      updated: '内核更新成功',
      uploadVariant: '上传变体包',
      viewVariants: '查看变体',
      variantAdded: '变体包上传成功',
      variantsDetected: '检测到 {count} 个变体包',
      packageFileTooLarge: '变体包中包含超过 1GB 的文件，整个包无法上传',
      variantUploadInvalid: '所选文件夹中未找到有效的 metadata.json',
      variantVersionMismatch: 'metadata.json 中的 name/version 必须与当前内核版本一致',
      noFolderSelected: '请选择文件夹或文件进行上传',
      license: '许可证',
      latestVersion: '最新版本',
      archs: '支持的架构',
      pythonDepends: 'Python 依赖',
      versions: '版本',
      noKernels: '暂无内核',
      createFirst: '上传您的第一个内核',
      deleteConfirm: '确定要删除此内核吗？所有版本和文件都将被删除。',
      deleteVersionConfirm: '确定要删除此版本吗？所有变体和文件都将被删除。',
      deleteVariantConfirm: '确定要删除此变体吗？',
      deleteFileConfirm: '确定要删除此文件吗？',
      viewFiles: '查看文件',
      versionDeleted: '版本删除成功',
      kernelDeleted: '内核删除成功',
      manageKernels: '管理内核',
      metadataPreview: '元数据预览',
      kernelId: '内核 ID',
      lastUpdated: '最近更新',
      backendType: '后端',
      digest: '摘要',
      variantImmutableTip: '同一 (version, variant) 只能发布一次。如需更新请修改 build.toml 中的版本号后重新构建。',
      versionNumber: '版本号',
      manageVersions: '版本管理',
    },
    home: {
      // Hero
      heroEyebrow: '<span>EMBODIED AI PLATFORM</span><b>具身智能全栈解决方案</b>',
      heroTitle: '让智能<br/>真正<span>进入现实</span>',
      heroSub: '连接芯片、模型与机器人本体，以 FlashRT 高性能推理引擎为核心，为真实世界的每一次感知、决策与行动加速。',
      heroExplore: '探索平台',
      heroViewSolutions: '查看解决方案',
      heroStatPlatforms: '计算平台',
      heroStatModels: '模型适配',
      heroStatKernels: '高性能 Kernels',
      heroVisualAria: 'FlashHub 技术栈示意图',
      heroCardMultimodal: '多模态模型',
      heroCardCompute: '异构计算',
      heroCardEmbodied: '具身本体',
      // Manifesto
      manifestoTitle: '一套平台，贯通具身智能<br/><span>从算力到底层动作</span>',
      platformLayerHardware: '硬件适配层',
      platformLayerHardwareDesc: '统一异构算力接口',
      platformLayerEngine: 'FlashRT 引擎',
      platformLayerEngineDesc: 'Kernel 融合与图执行',
      platformLayerServing: '模型服务层',
      platformLayerServingDesc: 'VLA / VLM / 世界模型',
      platformLayerRobot: '机器人接入层',
      platformLayerRobotDesc: '感知、控制与数据闭环',
      // Ecosystem
      ecoTitle: '开放的具身生态中心',
      ecoDesc: '将主流硬件、前沿模型与机器人本体组件化，<br/>像搭积木一样构建具身应用。',
      ecoChipsTitle: '计算芯片',
      ecoChipsDesc: '覆盖云、边、端的异构计算平台',
      ecoModelsTitle: '模型中心',
      ecoModelsDesc: '开箱即用的具身与物理 AI 模型',
      ecoBodiesTitle: '机器人本体',
      ecoBodiesDesc: '适配主流形态，统一设备接入',
      chipStatePriority: '重点适配',
      chipStateSupported: '已支持',
      chipStateInProgress: '适配中',
      chipStatePlanned: '规划中',
      chipStateVerifying: '验证中',
      chipSpecThor: 'Blackwell GPU · 具身计算',
      chipSpecOrin: 'Ampere GPU · 边缘推理',
      chipSpecAscend: 'Atlas · 国产算力',
      chipSpecHorizon: 'BPU · 端侧智能',
      chipSpecRB5: 'QRB5165 · Robotics',
      chipSpecIntel: 'CPU + GPU + NPU',
      modelDescPI0: '通用机器人视觉—语言—动作模型',
      modelDescGR00T: '面向人形机器人的基础模型',
      modelDescCosmos: '物理 AI 世界基础模型平台',
      modelDescOpenVLA: '开放的视觉语言动作模型',
      modelDescRDT: '双臂机器人扩散策略模型',
      modelDescQwen: '感知、理解与任务规划底座',
      robotOrgAGIBOT: '智元机器人',
      robotOrgUnitree: '宇树科技',
      robotOrgDeepRobotics: '云深处科技',
      robotOrgUBTECH: '优必选',
      robotOrgGalbot: '银河通用',
      robotTypeHumanoidWheeled: '人形 / 轮式',
      robotTypeHumanoid: '人形机器人',
      robotTypeQuadruped: '四足机器人',
      robotTypeIndustrial: '工业人形',
      robotTypeWheeledArm: '轮式双臂',
      robotTypeCobot: '协作机械臂',
      // FlashRT
      flashrtTitle: '为物理 AI 而生的<br/><span>极致推理引擎</span>',
      rtMetricFfn: '端到端 FFN 加速',
      rtMetricKernels: '算子与融合 Kernel',
      rtMetricOverhead: 'Runtime 调度开销',
      kernelPanelTitle: 'Kernel & Runtime SO 目录',
      kernelPanelSub: '面向不同芯片的可部署运行时组件',
      chipFilterAll: '全部芯片',
      kernelColComponent: '组件 / Kernel',
      kernelColType: '类型',
      kernelColSo: 'Runtime SO',
      kernelColTargets: '支持芯片',
      kernelColStatus: '状态',
      kernelRepoLink: '在 GitHub 查看 FlashRT-HF-kernels',
      // Solutions
      solutionsTitle: '走进真实世界的解决方案',
      solutionsDesc: '不止于技术组件。我们把模型、算力和本体组合成<br/>可验证、可复制、可运营的行业方案。',
      solCoffeeTitle: '无人咖啡零售',
      solCoffeeMeta: '具身操作 · 连锁商业',
      solCoffeeDesc: '从点单、抓取杯具到制作、交付与补货提醒，让标准化饮品服务 7×24 小时运行。',
      solCoffeeFlow1: '识别订单',
      solCoffeeFlow2: '精准操作',
      solCoffeeFlow3: '异常接管',
      solInspectTitle: '商业综合体巡检',
      solInspectMeta: '多模态感知 · 物业',
      solInspectDesc: '机器人自主巡逻，识别消防、设备、环境与客流异常，联动工单平台形成处置闭环。',
      solInspectFlow1: '自主导航',
      solInspectFlow2: '异常识别',
      solInspectFlow3: '闭环工单',
      solCareTitle: '智慧养老陪护',
      solCareMeta: '人机交互 · 康养',
      solCareDesc: '提供陪伴沟通、生命体征辅助监测、跌倒预警与物品递送，提升照护响应效率。',
      solCareFlow1: '自然交互',
      solCareFlow2: '风险预警',
      solCareFlow3: '生活协助',
      solFactoryTitle: '工业柔性作业',
      solFactoryMeta: 'VLA · 制造',
      solFactoryDesc: '面向上下料、分拣、质检与装配任务，用统一平台降低跨产线迁移与二次开发成本。',
      solFactoryFlow1: '示教学习',
      solFactoryFlow2: '柔性抓取',
      solFactoryFlow3: '质量追溯',
      learnMore: '了解方案',
      // Developer
      devTitle: '从一个 Kernel 开始<br/>构建你的具身应用',
      devDesc: '提供 SDK、模型配方、性能基准、部署指南与开放源码，让从验证到量产的每一步都有迹可循。',
      devGithub: '访问 GitHub',
      devBrowse: '浏览组件目录',
      // Contact
      contactTitle: '一起，把具身智能<br/>带到下一个现场。',
      contactDesc: '告诉我们你的行业、机器人本体与目标任务，<br/>获取从 PoC 到规模部署的完整建议。',
      contactBtn: '预约方案沟通',
      // Footer
      footerTagline: '端侧AI应用分发社区',
      footerTaglineSub: 'Edge AI App Distribution Community',
      footerColPlatform: '平台',
      footerLinkChips: '计算芯片',
      footerLinkModels: '模型中心',
      footerLinkBodies: '机器人本体',
      footerColTech: '技术',
      footerLinkEngine: 'FlashRT 引擎',
      footerLinkDev: '开发者中心',
      footerColSolutions: '解决方案',
      footerLinkRetail: '无人零售',
      footerLinkInspection: '智慧巡检',
      footerLinkCare: '康养陪护',
      footerColContact: '联系',
      footerLinkBusiness: '商务合作',
      footerLinkAbout: '关于我们',
      footerTag: '端侧AI应用分发社区',
    },
    docs: {
      title: '内核文档',
      subtitle: 'FlashHub 内核的目录结构、metadata.json 字段与构建变体命名规范（与 Hugging Face Kernels 规范完全兼容）。',
      viewBuildGuide: '查看构建文档',
    },
    dashboard: {
      allRepos: '全部仓库',
      latestVersion: '最新版本',
      sortBy: '排序方式',
      sortByUpdated: '更新时间',
      sortByCreated: '创建时间',
      sortByDownloads: '下载量',
    },
    organization: {
      createOrganization: '创建组织',
      basicInfo: '基本信息',
      orgName: '组织名称',
      displayName: '显示名称',
      description: '组织描述',
      logo: '组织Logo',
      uploadLogo: '上传Logo',
      logoTip: '推荐尺寸：200x200px，支持 JPG、PNG',
      contactInfo: '联系信息',
      website: '网站',
      email: '邮箱',
      location: '位置',
      enterOrgName: '请输入组织名称',
      enterDisplayName: '请输入显示名称',
      displayNameTip: '该名称将公开显示，支持中文和特殊字符',
      descriptionPlaceholder: '请输入组织描述（最多500字）',
      descriptionTip: '简要描述组织的目的和方向',
      enterWebsite: '请输入网站地址',
      enterContactEmail: '请输入联系邮箱',
      enterLocation: '请输入组织位置',
      visibility: '可见性',
      publicOrg: '公开组织',
      privateOrg: '私有组织',
      publicOrgDesc: '任何人都可以发现和查看该组织',
      privateOrgDesc: '只有成员可以查看该组织',
      members: '成员',
      inviteMembers: '邀请成员',
      role: '角色',
      name: '姓名',
      joinedAt: '加入时间',
      emails: '邮箱地址',
      emailsPlaceholder: '输入邮箱地址，用逗号、分号或换行分隔',
      emailsTip: '可以一次输入多个邮箱地址',
      emailsRequired: '请至少输入一个邮箱地址',
      sendInvites: '发送邀请',
      invitesSent: '邀请已发送',
      rolePermissions: '角色权限说明',
      ownerPerms: '全部权限：管理组织、成员、仓库，删除组织',
      adminPerms: '管理成员，创建仓库',
      developerPerms: '创建和管理仓库',
      viewerPerms: '仅查看仓库',
      createFirst: '创建第一个组织',
      deleteConfirm: '此操作不可恢复，确定要删除吗？',
      deleted: '组织已删除',
      settingsSaved: '设置已保存',
      checking: '正在检查可用性...',
      nameAvailable: '该组织名称可用',
      nameTaken: '该名称已被占用',
      nameInvalid: '仅允许字母、数字、短横线和下划线',
      orgNameHint: '仅允许字母、数字、短横线和下划线',
      orgNameRequired: '组织名称为必填项',
      displayNameRequired: '显示名称为必填项',
      orgCreated: '组织创建成功',
      noDescription: '暂无描述',
      editSettings: '编辑组织',
      organizations: '我的组织',
      backToOrganizations: '返回组织列表',
      noOrganizations: '暂无组织',
      organizationDetail: '组织详情',
    },
    public: {
      publicRepos: '公开仓库',
      searchPlaceholder: '按名称搜索仓库...',
      heroTitle: 'AI 模型推理加速引擎',
      heroSubtitle: '基于 FlashRT 部署和运行 AI 模型 — 快速、便携、生产级推理。',
      sortNewest: '最新',
      sortDownloads: '最多下载',
      sortStars: '最多星标',
      noDescription: '暂无描述',
      noTags: '暂无标签',
      emptyHint: '创建您的第一个仓库开始使用',
      downloads: '下载量',
      stars: '星标',
      forks: '派生',
      all: '全部',
    },
    nav: {
      home: '首页',
      apps: '应用',
      models: '模型',
      kernels: '内核',
      news: '资讯',
      ecosystem: '生态',
      cliDesc: 'FlashRT 命令行分发工具',
      agentName: 'AI 助手',
      agentDesc: '让 AI 助手自动把模型上架',
      rtDesc: '高性能推理引擎',
    },
    agent: {
      title: 'FlashHub AI 助手',
      desc: '让 AI 助手自动把模型上架到 FlashHub',
      chooseMode: '选择执行方式',
      standard: '标准版',
      standardDesc: '每一步执行前，AI 会先征求你的确认。',
      auto: '自动执行版',
      autoDesc: 'AI 直接完成上架，无需逐步确认，完成后汇报结果。',
      promptLabel: '发送给 AI 的指令',
      standardPrompt: '阅读 https://flashhub.aodianyun.com/agent/AGENTS.md，用 USER_KEY=fh_xxx 把模型上架到 FlashHub',
      autoPrompt: '阅读 https://flashhub.aodianyun.com/agent/AGENTS-auto.md，用 USER_KEY=fh_xxx 把模型上架到 FlashHub',
      hint: '支持 pi / Claude Code / Codex / opencode 等任意 AI Agent。AI 会自动完成：查 GPU 型号/平台/标签 → 建组织 → 建仓库 → 建版本 → 上传文件。',
      apiKeyTitle: '用户 API Key',
      apiKeyDesc: '在 用户中心 → API 密钥 创建，勾选以下 scope：',
    },
    apps: {
      title: '应用',
      subtitle: '按场景查找通用 Agent、专用 Agent 与音视频制作工具。',
      install: '安装 FlashCLI',
      all: '全部',
      count: '个应用',
      view: '查看应用',
      tool: '工具',
      toolTag: '工具 · 全站通用',
      toolDesc: '查找、安装、运行和更新模型与应用；版本跟随版本公告。',
    },
    homeFx: {
      kickerA: '从实验室到生产环境',
      kickerB: '端侧 AI 适配社区',
      h1a: '探索端侧 AI',
      h1b: '应用、模型与芯片',
      h1c: '的极致性能',
      heroDesc: '从应用场景出发，探索模型方案与硬件适配，通过 FlashCLI 在你的设备上运行。也可以从已有模型或硬件开始查找。',
      searchPh: '搜索应用、模型、芯片或设备',
      search: '搜索',
      exploreApps: '探索应用',
      browseModels: '浏览模型',
      iHaveHw: '我已有硬件',
      hcApp: '应用',
      hcModels: '关联模型',
      hcDevice: '设备 · 芯片',
      hcStatus: '应用状态',
      hcCta: '查看应用与运行方案',
      s1Eyebrow: '01 · APPS',
      s1Title: '从一个应用开始',
      s1Desc: '探索应用效果，找到所需模型与适配硬件。',
      viewAllApps: '查看全部应用',
      dependsOn: '依赖模型',
      adaptedDevices: '适配设备',
      viewApp: '查看应用',
      s2Eyebrow: '02 · MODELS',
      s2Title: '为你的应用选择模型',
      s2Desc: '按任务查看模型能力、关联应用与已适配硬件。',
      viewAllModels: '查看全部模型',
      all: '全部',
      relatedApps: '关联应用',
      adaptedHw: '已适配硬件',
      viewModel: '查看模型',
      none: '暂无',
      s3Eyebrow: '03 · HARDWARE',
      s3Title: '你的硬件，可以运行哪些 AI 应用？',
      s3Desc: '选择具体设备，查看可运行应用与已适配模型。',
      viewAllHw: '查看全部硬件',
      brandFilter: '按品牌筛选',
      otherChips: '其他已收录芯片 · 暂无设备与适配记录',
      addHw: '补充设备或芯片',
      currentDevice: '当前设备',
      chipSpecs: '芯片规格',
      deviceDetail: '设备详情',
      runnableApps: '可运行应用',
      pendingApps: '待适配应用',
      adaptedModels: '已适配模型',
      adaptationRecord: '适配记录',
      noReadyApps: '该设备暂无完成整体验证的应用。',
      noRecords: '该设备暂无适配记录。',
      verifiedNote: '模型适配状态与应用整体状态分别验证',
      s4Eyebrow: '04 · QUICK START',
      s4Title: '将选好的应用运行在你的设备上',
      s4Desc: '命令对应所选应用、模型方案、设备与适配版本；没有可靠命令时提供文档与申请入口。',
      step1t: '选择应用',
      step1d: '从效果出发挑选应用',
      step2t: '确认模型与硬件方案',
      step2d: '在应用详情中切换方案与设备',
      step3t: '通过 FlashCLI 获取并运行',
      step3d: '复制对应组合的命令',
      installCli: '安装 FlashCLI',
      quickStart: '查看快速开始',
      app: '应用',
      plan: '模型方案',
      device: '设备',
      version: '适配版本',
      adjustPlan: '在应用详情中调整方案',
      noCmdTitle: '该组合暂无可靠的安装命令',
      viewDocs: '查看运行文档',
      applyAdaptation: '申请适配',
      s5Eyebrow: '05 · UPDATES',
      s5Title: '最新适配',
      communityEyebrow: 'COMMUNITY',
      communityTitle: '社区实践',
      enterCommunity: '进入社区',
      noNeed: '没有找到需要的应用、模型或硬件？',
      submitRequest: '提交适配需求',
      newVerified: '新增已验证适配',
      submittedVersion: '提交适配版本',
      replies: '条回复',
    },
    news: {
      heading: '资讯与动态',
      subtitle: '记录 Flash Hub 的产品发布、技术分享与具身智能行业洞察。',
      searchPlaceholder: '输入标题关键词搜索资讯…',
      all: '全部资讯',
      empty: '暂无资讯',
      emptyHint: '敬请期待后续更新',
      views: '阅读',
      categories: {
        release: '产品发布',
        tech: '技术分享',
        case: '案例',
        insight: '行业洞察',
      },
      back: '返回资讯',
      notFound: '资讯不存在',
      notFoundHint: '该文章可能已被删除或未发布。',
    },
    validation: {
      required: '此项必填',
    },
    confirm: {
      deleteTitle: '确认删除',
      deleteMessage: '确定要删除此项吗？',
      logoutTitle: '确认',
      logoutMessage: '确定要退出登录吗？',
    },
    notFound: {
      title: '404',
      subtitle: '页面未找到',
      backHome: '返回首页',
    },
    footer: {
      resources: '资源',
      company: '公司',
      contact: '联系我们',
      docs: '文档',
      help: '帮助中心',
      community: '社区',
      about: '关于我们',
      careers: '招聘',
    },
    flashcli: {
      subtitle: 'FlashRT 的分发 CLI — 一条命令拉取 Model Bundle、安装依赖、下载权重并运行推理。',
      featuresTitle: '核心能力',
      quickstart: {
        title: '快速开始',
        install: '安装',
      },
      commands: {
        run: { label: '运行', desc: '一条命令运行 VLA 推理任务' },
        pull: { label: '拉取模型权重', desc: '从 Hugging Face 预拉取模型权重' },
        models: { label: '查看本地模型', desc: '查看预设目录和本地缓存状态' },
        doctor: { label: '环境诊断', desc: '环境和 GPU 兼容性诊断' },
      },
      features: {
        bundle: { title: 'Bundle 分发', desc: '自动检测 GPU 并从 CDN 拉取匹配的运行时 Bundle，无需手动配置。' },
        gpu: { title: 'GPU 自动匹配', desc: '根据 NVIDIA GPU 架构（SM89、SM120）选择正确的预编译 Bundle 变体。' },
        autodeps: { title: '依赖自动安装', desc: '读取 Bundle 元数据，自动安装 Python 依赖、模型权重和分词器。' },
        oneclick: { title: '一键推理', desc: '一条 flashcli run 命令完成 Bundle 下载、权重拉取和推理启动。' },
        cache: { title: '本地缓存', desc: 'Bundle 缓存至 ~/.flashcli/bundles，权重存至 ~/.flashcli/models，无需重复下载。' },
        cli: { title: '完整 CLI 工具集', desc: '支持 run、pull、doctor、bundle 管理等子命令和环境诊断。' },
      },
      commandRefTitle: '命令参考',
      ref: {
        run: '运行预设推理任务（如 pi05_libero VLA 任务）',
        pull: '仅预拉取 Hugging Face 权重',
        modelsList: '显示预设目录和本地缓存状态',
        modelsEnvs: '查看预设对应的 GPU 环境匹配',
        doctor: '检查 GPU、Python 和系统兼容性',
        bundleSync: '预下载/更新运行时 Bundle',
        bundleValidate: '校验本地 Bundle 目录结构',
      },
      cacheTitle: '本地缓存',
      cache: {
        bundles: '已解压的运行时 Bundle 文件',
        models: 'Hugging Face 模型权重',
        tokenizer: 'PaliGemma 分词器（post_pull）',
      },
    },
    admin: {
      management: '管理',
      resourceManagement: '资源上架申请',
      resourceNew: '新增资源申请',
      resourceEdit: '编辑资源申请',
      resourceDetailTitle: '资源申请详情',
      resource: {
        desc: '统一收集应用、模型和芯片/硬件资料，分配负责人并跟进上架准备。',
        all: '全部',
        app: '应用',
        model: '模型',
        hardware: '芯片／硬件',
        searchPlaceholder: '搜索名称、团队或联系人',
        statusAll: '全部状态',
        ownerAll: '全部负责人',
        originAll: '全部来源',
        mineOnly: '待我跟进',
        clearFilters: '清除筛选',
        total: '共',
        items: '条',
        incomplete: '资料待补充',
        dueToday: '今日及逾期待跟进',
        draft: '草稿',
        export: '导出记录',
        create: '新增申请',
        columnName: '资源名称',
        columnType: '资源类型',
        columnOrg: '所属团队／厂商',
        columnCompleteness: '资料情况',
        columnStatus: '当前状态',
        columnRsiStatus: 'RSI状态',
        columnOwner: '内部负责人',
        columnContact: '对方联系人',
        columnContactWay: '联系方式',
        columnNextAction: '下一步动作',
        columnNextDate: '下次跟进',
        columnActions: '操作',
        exportFilename: '资源上架申请',
        exportSuccess: '已导出 {count} 条记录',
        deleteConfirm: '确定要删除「{name}」吗？此操作不可恢复。',
        deleteTitle: '删除资源申请',
        deleted: '已删除',
        view: '查看',
        edit: '编辑',
        delete: '删除',
        notFilled: '未填写',
        unassigned: '未分配',
        noData: '暂无资源上架申请',
        completenessComplete: '基本完整',
        completenessIncomplete: '待补充',
        missing: {
          intro: '一句话介绍',
          org: '所属团队／厂商',
          websiteOrRepo: '官网或项目地址',
          sources: '资料来源链接',
          detail: '详细介绍',
          appCategory: '应用类别',
          appUsage: '主要用途及场景',
          appModels: '关联模型',
          appDeploy: '部署方式',
          appOs: '操作系统与运行环境',
          appHardware: '硬件要求或运行设备',
          modelCategory: '模型类别',
          modelName: '模型名称及版本',
          modelWeights: '权重下载地址',
          modelLicense: '使用许可说明',
          modelDeployNotes: '运行与部署说明',
          modelHardwares: '关联芯片/硬件',
          gpuSeries: 'GPU 系列',
          arch: '硬件架构标识',
          gpuVendor: 'GPU 厂商',
          backend: '计算运行后端',
          deviceType: '设备类型',
          coreSpecs: '核心规格',
          specSheet: '产品规格书',
          sdk: 'SDK／开发工具',
          contact: '对方联系人',
        },
        origin: {
          internal: '内部采集',
          external: '外部提供',
        },
        logWays: {
          wechat: '微信',
          email: '邮件',
          phone: '电话',
          meeting: '会议',
          other: '其他',
        },
        status: {
          pending: '待采集',
          collecting: '采集中',
          reviewing: '待审核',
          ready: '可上架',
          hold: '暂缓',
        },
        rsiStatus: {
          idle: '等待',
          pending: '待处理',
          processing: '处理中',
          done: '已完成',
          failed: '失败',
        },
      },
      resourceDetail: {
        back: '资源上架申请',
        draft: '草稿',
        noIntro: '尚未填写一句话介绍',
        more: '更多操作',
        delete: '删除',
        edit: '编辑',
        addLog: '新增沟通记录',
        basicInfo: '基础资料',
        teamVendor: '所属团队／厂商',
        origin: '提交来源',
        website: '官方网站',
        repo: '项目地址',
        detail: '详细介绍',
        typeSpecific: '专属资料',
        appCategory: '应用类别',
        deploy: '部署方式',
        usage: '主要用途及场景',
        appModels: '关联模型',
        osEnv: '操作系统与运行环境',
        hardwareRequirement: '硬件要求或运行设备',
        demoUrl: '演示地址',
        modelCategory: '模型类别',
        modelName: '具体模型名称',
        modelVersion: '版本',
        modelParams: '参数规模',
        modelPrecision: '精度或量化版本',
        license: '使用许可',
        weights: '权重下载地址',
        knownApps: '已知应用',
        knownHardware: '已知适配硬件',
        deployNotes: '运行与部署说明',
        modelHardwares: '关联芯片/硬件',
        gpuSeries: 'GPU 系列',
        arch: '硬件架构标识',
        gpuVendor: 'GPU 厂商',
        backend: '计算运行后端',
        deviceType: '设备类型',
        specSheet: '产品规格书',
        sdk: 'SDK／开发工具',
        supportedModels: '已支持的模型',
        runningApps: '已运行的应用',
        adaptationNotes: '适配说明',
        coreSpecs: '核心规格',
        unnamed: '未命名',
        notFilled: '未填写',
        attachmentsSources: '附件与来源',
        sources: '资料来源',
        attachments: '附件',
        noAttachments: '暂无附件',
        commLog: '沟通记录',
        noCommLog: '暂无沟通记录',
        withTarget: '与',
        recordedBy: '记录人',
        nextStep: '下一步',
        followPanel: '跟进面板',
        currentStatus: '当前状态',
        rsiStatus: 'RSI 状态',
        retryRsi: '重新提交 RSI',
        missingItems: '资料缺失项',
        complete: '资料基本完整',
        followUpDesc: '内部负责人是我方跟进人；对方联系人是资源提供方的对接人。',
        owner: '内部负责人',
        contact: '对方联系人',
        nextAction: '下一步动作',
        nextDate: '下次跟进日期',
        note: '内部备注',
        unassigned: '未分配',
        logTime: '沟通时间',
        logTarget: '沟通对象',
        logTargetPlaceholder: '对方姓名或团队',
        logWay: '沟通方式',
        logConclusion: '沟通结论',
        logConclusionPlaceholder: '对方确认了什么、提供了哪些资料、还有什么问题',
        logNext: '下一步动作',
        logSync: '同步更新跟进面板的“下一步动作”和跟进日期',
        logFine: '仅做手动记录，不会自动向对方发送消息。',
        saveLog: '保存记录',
        logRequired: '请填写沟通结论',
        logSaved: '已新增沟通记录',
        statusUpdated: '状态已更新为',
        retryConfirm: '确定要重新提交「{name}」给 RSI 处理吗？',
        retryTitle: '重新提交 RSI',
        retryConfirmBtn: '重新提交',
        retrySaved: '已重新提交 RSI，状态已改为待处理',
        deleteConfirm: '确定要删除「{name}」吗？此操作不可恢复。',
        deleteTitle: '删除资源申请',
        deleted: '已删除',
        recordNotFound: '记录不存在',
        backToList: '返回列表',
      },
      resourceForm: {
        backNew: '返回列表',
        backEdit: '返回详情',
        newTitle: '新增资源申请',
        editTitle: '编辑：{name}',
        saveHint: '资料不完整时也可以保存草稿。核心必填项：资源类型、资源名称、至少一个资料来源链接。',
        typeSwitch: '随资源类型切换。',
        name: '资源名称',
        namePlaceholder: '例如：离线会议纪要工具',
        intro: '一句话介绍',
        introPlaceholder: '20–40 字说明它是什么、解决什么问题',
        orgLabel: '所属团队／公司／厂商',
        sources: '资料来源链接',
        addSource: '添加来源链接',
        sourceHelp: '填写信息出处，例如官方文档、规格页、对方提供的资料地址。',
        detail: '详细介绍',
        detailPlaceholder: '功能特点、技术方案、合作背景等',
        attachments: '附件上传',
        chooseFile: '选择文件',
        attachmentsHelp: '仅记录文件名，不会真正上传。',
        appCategory: '应用类别',
        appDeploy: '部署方式',
        appUsage: '主要用途及使用场景',
        appModels: '关联模型',
        modelResource: '模型资源',
        addModel: '添加模型',
        appModelsHelp: '从已有模型资源中选择，可多选。',
        osEnv: '操作系统与运行环境',
        appHardware: '硬件要求或已有运行设备',
        demoUrl: '演示地址',
        modelCategory: '模型类别',
        modelName: '具体模型名称',
        modelVersion: '版本',
        modelParams: '参数规模',
        modelPrecision: '精度或量化版本',
        weights: '权重下载地址',
        license: '使用许可说明链接',
        knownApps: '已知使用该模型的应用',
        knownHardware: '已知适配硬件',
        deployNotes: '运行与部署说明',
        modelHardwares: '关联芯片/硬件',
        chipHardwareResource: '芯片/硬件资源',
        addChipHardware: '添加芯片/硬件',
        modelHardwaresHelp: '从已有芯片/硬件资源中选择，可多选。',
        gpuSeries: 'GPU 系列',
        arch: '硬件架构标识',
        gpuVendor: 'GPU 厂商',
        backend: '计算运行后端',
        deviceType: '设备类型',
        coreSpecs: '核心规格',
        specName: '参数名称，例如：显存',
        specValue: '参数值，例如：12GB GDDR6',
        addSpec: '添加参数',
        specSheet: '产品规格书',
        sdk: 'SDK／开发工具地址',
        supportedModels: '已支持的模型',
        runningApps: '已运行的应用',
        adaptationNotes: '适配说明',
        followUpDesc: '内部负责人是我方跟进人；对方联系人是资源提供方的对接人。',
        owner: '内部负责人',
        ownerPlaceholder: '填写跟进人姓名',
        currentStatus: '当前状态',
        contactName: '对方联系人',
        contactNamePlaceholder: '未建立联系可填写“未建立联系”',
        contactTitle: '职位或团队',
        contactWay: '联系方式',
        contactWayPlaceholder: '微信 / 邮件 / 电话 / 社区渠道',
        nextDate: '下次跟进日期',
        nextAction: '下一步动作',
        nextActionPlaceholder: '例如：索取规格书、确认芯片/硬件适配清单',
        note: '内部备注',
        progressTitle: '填写进度',
        missingCount: '仍缺少 {count} 项资料',
        complete: '资料基本完整',
        draftHint: '草稿可随时保存；提交审核时会再次提示缺失项。',
        lastUpdated: '最后更新 {date}',
        newRecord: '新建记录',
        cancel: '取消',
        saveDraft: '保存草稿',
        submitReview: '提交审核',
        confirmTitle: '仍有资料未补充',
        confirmLead: '以下资料尚未填写，审核人可能会退回补充：',
        backFix: '返回补充',
        submitAnyway: '仍然提交审核',
        pickerModelTitle: '选择关联模型',
        pickerHardwareTitle: '选择关联芯片/硬件',
        searchModel: '搜索模型名称',
        searchHardware: '搜索芯片/硬件名称',
        noModel: '暂无模型资源',
        noHardware: '暂无芯片/硬件资源',
        confirm: '确定',
        nameRequired: '请填写资源名称',
        sourcesRequired: '至少填写一个资料来源链接',
        fillRequired: '请先补充必填项',
        draftSaved: '已保存草稿',
        submitted: '已提交审核',
        typeApp: '可运行的应用或工具',
        typeModel: '模型及其版本、权重',
        typeHardware: 'GPU、芯片、开发板或整机方案',
        modelNamePlaceholder: '例如：SenseVoice-Small',
        modelVersionPlaceholder: '例如：2024-07',
        modelParamsPlaceholder: '例如：1.5B',
        modelPrecisionPlaceholder: '例如：FP16 / INT4',
        deployNotesPlaceholder: '例如：需导出 ONNX 后再转换为目标 NPU 格式。',
        gpuSeriesPlaceholder: '例如：NVIDIA RTX 30 系列',
        archPlaceholder: '例如：sm86',
        gpuVendorPlaceholder: '例如：Nvidia',
        gpuVendorOtherPlaceholder: '请填写具体 GPU 厂商',
        backendPlaceholder: '例如：cuda',
        backendOtherPlaceholder: '请填写具体计算运行后端',
        deviceTypePlaceholder: '例如：gpu',
        deviceTypeOtherPlaceholder: '请填写具体设备类型',
        specSheetPlaceholder: '上传文件名或填写链接',
        supportedModelsPlaceholder: '例如：YOLOv8n（厂商自测）',
        select: '请选择',
      },
      model: {
        desc: '管理平台上的模型仓库、组织，以及 GPU / 平台 / 标签。',
        tabRepos: '模型列表',
        tabOrgs: '组织列表',
        tabCatalog: 'GPU/平台/标签',
        searchRepo: '搜索仓库名',
        searchOrg: '搜索组织名',
        allStatus: '全部状态',
        public: '公开',
        private: '私有',
        offline: '已下架',
        search: '查询',
        repoName: '仓库名',
        org: '组织',
        platformModel: '平台模型',
        gpu: 'GPU',
        status: '状态',
        createdAt: '创建时间',
        actions: '操作',
        takeOffline: '下架',
        putOnline: '上架',
        delete: '删除',
        orgName: '组织名',
        displayName: '显示名',
        repoKernelCount: '模型/内核数',
        memberCount: '成员数',
        enabled: '启用',
        disabled: '已禁用',
        disable: '禁用',
        enable: '启用',
        addGpu: '新增 GPU',
        addPlatform: '新增平台',
        addTag: '新增标签',
        gpuId: 'GPU 标识',
        description: '描述',
        sortOrder: '排序',
        edit: '编辑',
        platformId: '标识',
        tagId: '标识',
        tagNameCn: '中文名',
        color: '颜色',
        gpuNamePlaceholder: '例如 sm89-cu130-linux-x86_64-py312',
        gpuDescPlaceholder: '例如 GeForce RTX 40 Series, L40S Series',
        platformNamePlaceholder: '例如 huggingface',
        platformDisplayPlaceholder: '例如 HuggingFace',
        tagNamePlaceholder: '例如 LLM',
        tagIconPlaceholder: '例如 ri-robot-line',
        tagColorPlaceholder: '例如 #2563eb',
        gpuTitle: 'GPU 型号',
        platformTitle: '平台',
        tagTitle: '标签',
        editGpuTitle: '编辑 GPU 型号',
        addGpuTitle: '新增 GPU 型号',
        editPlatformTitle: '编辑平台',
        addPlatformTitle: '新增平台',
        editTagTitle: '编辑标签',
        addTagTitle: '新增标签',
        gpuIdHelp: 'GPU 标识（命名规范：<arch>-<runtime>-<os>-<cpuarch>-py<python>）',
        icon: '图标 URL',
        iconHelp: '图标（remixicon 类名）',
        save: '保存',
        cancel: '取消',
        updated: '已更新',
        deleted: '已删除',
        saved: '已保存',
        deleteTitle: '删除确认',
        deleteRepoConfirm: '确定删除仓库「{name}」？',
        deleteOrgConfirm: '确定删除组织「{name}」？该操作会同时影响其下模型和内核。',
        deleteGpuConfirm: '确定删除 GPU「{name}」？',
        deletePlatformConfirm: '确定删除平台「{name}」？',
        deleteTagConfirm: '确定删除标签「{name}」？',
      },
      newsManagement: '资讯管理',
      userManagement: '用户管理',
      modelManagement: '模型管理',
      apiKeys: {
        title: 'API 密钥',
        desc: '管理用于程序化访问开放接口的 API 密钥。',
        create: '创建 API 密钥',
        name: '名称',
        namePlaceholder: '例如：资源同步',
        key: '密钥',
        scopes: '权限范围',
        scopeResourcesRead: '读取资源上架申请',
        scopeResourcesWrite: '更新资源状态',
        scopeGpuWrite: '管理 GPU 型号',
        status: '状态',
        enabled: '已启用',
        disabled: '已停用',
        lastUsed: '最近使用',
        createdAt: '创建时间',
        actions: '操作',
        delete: '删除',
        deleteConfirm: '确定删除该 API 密钥？删除后无法恢复。',
        createdTitle: 'API 密钥已创建',
        createdHint: '请妥善保存此 API 密钥。关闭此窗口后将无法再次查看。如果丢失，需要重新创建。',
        copy: '复制',
        copied: '已复制',
        noKeys: '暂无 API 密钥',
      },
      news: {
        new: '新建资讯',
        edit: '编辑资讯',
        title: '标题',
        titlePlaceholder: '请输入资讯标题',
        category: '分类',
        categoryPlaceholder: '请选择分类',
        cover: '封面图',
        uploadCover: '上传封面',
        coverHint: '建议 16:9 比例，支持 JPG/PNG/WebP',
        summary: '摘要',
        summaryPlaceholder: '选填，留空将自动从正文提取',
        autoSummary: '将自动生成：',
        useAutoSummary: '填入',
        autoSummaryEmpty: '输入正文后将自动生成摘要',
        content: '正文内容',
        contentPlaceholder: '请输入正文…',
        publishTime: '发布时间',
        publishTimeHint: '留空则使用当前系统时间',
        status: '状态',
        published: '已发布',
        draft: '草稿',
        searchPlaceholder: '输入标题搜索',
        colTitle: '标题',
        colCategory: '分类',
        colPublishTime: '发布时间',
        colViews: '阅读数',
        colStatus: '状态',
        colActions: '操作',
        empty: '暂无资讯',
        editBtn: '编辑',
        preview: '预览',
        deleteBtn: '删除',
        deleteConfirmTitle: '删除资讯',
        deleteConfirmMessage: '删除后不可恢复，文章相关的封面图与正文图片也会一并删除。',
        save: '保存',
        saving: '保存中…',
        saveDraft: '保存草稿',
        draftSaved: '已保存为草稿',
        createFailed: '创建草稿失败',
        createSuccess: '资讯已发布',
        untitled: '未命名草稿',
        updateSuccess: '资讯已更新',
        deleteSuccess: '资讯已删除',
        titleRequired: '标题不能为空',
        contentRequired: '内容不能为空',
        loadFailed: '加载资讯失败',
      },
      changePassword: '修改密码',
      currentPassword: '当前密码',
      newPassword: '新密码（至少8位，含字母+数字）',
      dashboard: '管理后台',
      user: '用户',
      emailVerified: '邮箱验证',
      status: '状态',
      registeredAt: '注册时间',
      actions: '操作',
      verified: '已验证',
      unverified: '未验证',
      adminRole: '管理员角色',
      primaryAdmin: '主管理员',
      adminTag: '管理员',
      setAdmin: '设为管理员',
      removeAdmin: '取消管理员',
      setAdminSuccess: '已设为管理员',
      removeAdminSuccess: '已取消管理员',
      confirmRemoveAdmin: '确定取消此用户的管理员权限？',
      detail: {
        title: '用户详情',
        id: 'ID',
        username: '用户名',
        email: '邮箱',
        displayName: '显示名称',
        verified: '邮箱验证',
        status: '状态',
        bio: '简介',
        website: '网站',
        company: '公司',
        location: '位置',
        registeredAt: '注册时间',
        updatedAt: '更新时间',
      },
      totalUsers: '总用户数',
      active: '正常',
      disabled: '已禁用',
      searchPlaceholder: '搜索邮箱、用户名...',
      noUsers: '暂无用户',
      confirmDisableUser: '确定要禁用此用户？',
      disable: '禁用',
      enable: '启用',
      enableSuccess: '已启用',
      disableSuccess: '已禁用',
      passwordRequired: '请填写当前密码和新密码',
      passwordLength: '新密码至少8位',
      passwordChanged: '密码已修改',
      // Admin auth related
      adminLogin: '管理后台登录',
      adminEmail: '管理员邮箱',
      password: '密码',
      login: '登录',
      loginFailed: '登录失败，请检查邮箱和密码',
      loginSuccess: '登录成功',
      backHome: '返回首页',
      fillEmailPassword: '请输入邮箱和密码',
      analytics: {
        title: '下载分析',
        totalDownloads: '总下载量',
        avgDaily: '日均下载',
        activeDays: '活跃天数',
        dailyDownloadTrend: '每日下载趋势',
        downloads: '下载次数',
        byRepo: '按仓库统计',
        '7d': '7天',
        '14d': '14天',
        '30d': '30天',
        '90d': '90天',
        noData: '暂无下载数据',
        pvuvTitle: 'PV / UV 访问量',
        pvuvDesc: 'PV = 页面总访问量。UV = 独立访客数，同一设备同一浏览器只计为 1 个访客',
        site: '网站分析',
        avgPvPerUv: '人均PV',
        newVisitors: '新访客',
        bounceRate: '跳出率',
        cardPVDesc: '所选时段总页面访问量。↑↓本期vs上期',
        cardUVDesc: '所选时段去重访客数。↑↓本期vs上期',
        avgPvPerUvDesc: 'PV÷UV，人均浏览页面数',
        cardBounceDesc: '只看1页就走的占比，越低越好',
        visitorComposition: '访客构成',
        visitorDesc: '新访客：首次访问的访客。回访客：之前来过又回来的访客',
        returningVisitors: '回访客',
        engagement: '用户粘性',
        engageDesc: '人均PV = 平均每人浏览页面数（越高粘性越好）。跳出率 = 只看1页就走的占比（越低越好）',
        trafficSource: '访问来源',
        sourceDesc: '访客来源渠道：直接访问（输入网址/书签）、搜索引擎（Google/Bing/百度）、GitHub 或其它网站',
        device: '设备类型',
        deviceDesc: '根据浏览器标识识别的设备类型：桌面端、移动端或平板',
        country: '地域分布',
        countryDesc: '按 IP 地址识别的地理分布。国内精确到省份，国外显示国家。数据来源 ip2region',
        hourly: '时段分布',
        hourlyDesc: '按小时的页面访问量分布（服务器时间），可看出访问高峰时段',
        domain: '域名',
        landingPages: '落地页',
        landingDesc: '访客进入网站的首页面（本时段内首次访问的页面），反映流量入口分布',
        referers: '来源网站',
        referersDesc: '哪些外部网站给你带来了流量',
        retentionTitle: '留存率',
        retention: '次日留存',
        retentionDesc: '今天访问的访客中，明天再次访问的占比。越高说明用户留存越好',
        desktop: '桌面端',
        mobile: '移动端',
        tablet: '平板',
        direct: '直接访问',
        search: '搜索引擎',
        other: '其他',
        page: '页面',
      },
    },
  }
}

export type MessageKey = keyof typeof messages.en
export type Locale = keyof typeof messages

class I18n {
  private messages: Record<string, any>
  
  constructor() {
    this.messages = messages
    this.initLocale()
  }
  
  get locale(): 'en' | 'zh' {
    return currentLocale.value
  }
  
  set locale(val: 'en' | 'zh') {
    currentLocale.value = val
  }

  setLocale(locale: Locale) {
    currentLocale.value = locale
    localStorage.setItem('language', locale)
    localStorage.setItem('flashhub_language', locale)
    // Refresh document title to match new locale
    const suffix = locale === 'zh' ? 'AI 模型推理加速引擎' : 'AI Model Inference Runtime'
    const current = document.title
    const idx = current.lastIndexOf(' - ')
    if (idx > 0) {
      document.title = current.substring(0, idx) + ' - ' + suffix
    } else {
      document.title = 'Flash Hub - ' + suffix
    }
  }

  private initLocale() {
    const saved = localStorage.getItem('flashhub_language') || localStorage.getItem('language')
    if (saved === 'en' || saved === 'zh') {
      currentLocale.value = saved
    } else {
      // Default to English
      currentLocale.value = 'en'
    }
  }
  
  getLocale(): Locale {
    return currentLocale.value
  }
  
  t(key: string, params?: Record<string, any>): string {
    const locale = currentLocale.value
    const keys = key.split('.')
    let value: any = this.messages[locale]
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k]
      } else {
        // Fallback to English
        value = this.messages.en
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey]
          } else {
            return key // Return key if not found
          }
        }
        break
      }
    }
    
    if (typeof value === 'string') {
      // Replace parameters
      if (params) {
        return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
          return String(params[paramKey] || match)
        })
      }
      return value
    }
    
    return key
  }
}

export const i18n = new I18n()

export function useI18n() {
  return {
    t: i18n.t.bind(i18n),
    getLocale: i18n.getLocale.bind(i18n),
    setLocale: i18n.setLocale.bind(i18n),
  }
}

export default i18n